import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import './AdminGuestPortal.css';
import { AdminCompactOverview } from './AdminCompactOverview';
import { hasConfirmedTrainBooking } from './adminGuestFilters';
import {
  ADMIN_RSVP,
  ADMIN_RSVP_LABEL,
  computeRsvpStatusFromForm,
  DEFAULT_TAG_PRESETS,
  FORM_RSVP,
  formAndVerbalFromAdminRsvp,
  getGuestRegistry,
  GUEST_POOL,
  GUEST_SIDE,
  isRemoteGuestRegistryConfigured,
  isRemoteGuestRegistryHydrated,
  GUEST_REGISTRY_SHRINK_GUARD_BYPASS_SESSION_KEY,
  normalizeEmail,
  nextCoachOutValue,
  normalizeCoachSeatCount,
  normalizePlusOnes,
  normalizeTags,
  readGuestRegistryLocalStorageDraft,
  saveGuestRegistry,
  setGuestRegistryShrinkGuardBypass,
  SITE_VISIBILITY,
  sortGuestsForDisplay,
  subscribeGuestRegistry,
  VERBAL_RSVP,
  WEDDING_GUEST_REGISTRY_PERSISTED_OK_EVENT,
  WEDDING_GUEST_REGISTRY_SAVE_FAILED_EVENT,
} from './guestAllowlist';
import { downloadGuestRegistryBackup } from './guestAdminBackup';
import { downloadVendorGuestExport } from './guestVendorExport';
import { AdminMailoutsTab } from './AdminMailoutsTab';
import { AdminVoiceNotesTab } from './AdminVoiceNotesTab';
import { AdminCsvImportModal } from './AdminCsvImportModal';
import {
  AdminThemeProvider,
  getAdminTheme,
  readAdminColorMode,
  useAdminTheme,
  writeAdminColorMode,
} from './adminTheme';

function newGuestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `guest-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function defaultGuest() {
  return {
    id: newGuestId(),
    email: '',
    firstName: '',
    lastName: '',
    group: '',
    guestSide: GUEST_SIDE.UNSPECIFIED,
    dietary: '',
    notes: '',
    address: '',
    phone: '',
    accommodation: '',
    coachIn: null,
    coachOut: null,
    siteVisibility: SITE_VISIBILITY.CEREMONY_RECEPTION,
    pool: GUEST_POOL.INVITED,
    verbalRsvp: VERBAL_RSVP.UNSET,
    formRsvp: FORM_RSVP.PENDING,
    adminRsvpStatus: ADMIN_RSVP.NOT_INVITED,
    isGroupLead: false,
    /** Negative so new rows sort ahead of others in the same (empty) group. */
    sortOrder: -Date.now(),
    plusOnes: 0,
    tags: [],
    rsvpStatus: computeRsvpStatusFromForm(FORM_RSVP.PENDING),
    rsvpUpdatedAt: null,
    inviteUrlSent: false,
  };
}

function persistRegistry(guests) {
  Promise.resolve(saveGuestRegistry(guests)).catch(() => {});
}

/** Drop duplicate ids (first wins) so a bad merge cannot strand undeletable phantom rows. */
function dedupeGuestsById(guests) {
  if (!Array.isArray(guests)) return [];
  const seen = new Set();
  const out = [];
  for (const g of guests) {
    const id = g?.id;
    if (typeof id !== 'string' || !id || seen.has(id)) continue;
    seen.add(id);
    out.push(g);
  }
  return out;
}

/**
 * Avoids pushing every keystroke into the registry `guests` array (which re-ran filtering, tag palette, and
 * the whole sheet). Draft stays local; parent + debounced persist update on blur when the value changed.
 */
function AdminGuestDeferredTextInput({
  guestId,
  value,
  onCommit,
  type = 'text',
  className,
  style,
  placeholder,
  title,
  'aria-label': ariaLabel,
}) {
  const propStr = value == null ? '' : String(value);
  const [draft, setDraft] = useState(propStr);
  const focusedRef = useRef(false);

  useLayoutEffect(() => {
    if (focusedRef.current) return;
    setDraft(propStr);
  }, [guestId, propStr]);

  return (
    <input
      type={type}
      className={className}
      style={style}
      placeholder={placeholder}
      title={title}
      aria-label={ariaLabel}
      value={draft}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      onBlur={(e) => {
        focusedRef.current = false;
        const v = e.target.value;
        if (v !== propStr) onCommit(v);
      }}
    />
  );
}

const DIETARY_POPOUT_MARGIN = 8;

function computeDietaryPopoutBox(anchorRect) {
  const m = DIETARY_POPOUT_MARGIN;
  const vw = window.innerWidth;
  const viewport = window.visualViewport;
  const viewportTop = viewport?.offsetTop || 0;
  const vh = viewportTop + (viewport?.height || window.innerHeight);
  const minW = 220;
  const maxW = Math.min(440, vw - 2 * m);
  const width = Math.max(minW, Math.min(maxW, Math.max(anchorRect.width, minW)));
  let left = anchorRect.left;
  if (left + width > vw - m) left = vw - width - m;
  if (left < m) left = m;

  const panelChrome = 52;
  const textareaMin = 100;
  const minBlock = panelChrome + textareaMin;

  let top = anchorRect.bottom + m;
  let maxH = vh - m - top;

  if (maxH < minBlock) {
    const aboveTop = anchorRect.top - minBlock - m;
    if (aboveTop >= viewportTop + m) {
      top = aboveTop;
      maxH = vh - m - top;
    } else {
      top = viewportTop + m;
      maxH = vh - viewportTop - 2 * m;
    }
  }

  maxH = Math.max(minBlock, Math.min(maxH, vh - m - top));
  return { left, top, width, maxH };
}

/** Expandable dietary cell: tap opens a viewport-aware multi-line editor; tap away closes and saves. */
function AdminGuestDietaryPopoutInput({ value, onCommit, theme, 'aria-label': ariaLabel }) {
  const propStr = value == null ? '' : String(value);
  const [draft, setDraft] = useState(propStr);
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState({ left: 0, top: 0, width: 280, maxH: 240 });
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const textareaRef = useRef(null);
  const closingRef = useRef(false);
  const openRef = useRef(false);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const propStrRef = useRef(propStr);
  propStrRef.current = propStr;
  openRef.current = open;

  const syncBox = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    setBox(computeDietaryPopoutBox(el.getBoundingClientRect()));
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    syncBox();
  }, [open, syncBox]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', syncBox);
    window.visualViewport?.addEventListener('resize', syncBox);
    window.visualViewport?.addEventListener('scroll', syncBox);
    document.addEventListener('scroll', syncBox, true);
    return () => {
      window.removeEventListener('resize', syncBox);
      window.visualViewport?.removeEventListener('resize', syncBox);
      window.visualViewport?.removeEventListener('scroll', syncBox);
      document.removeEventListener('scroll', syncBox, true);
    };
  }, [open, syncBox]);

  useLayoutEffect(() => {
    if (open) return;
    setDraft(propStr);
  }, [propStr, open]);

  const finish = useCallback(() => {
    if (closingRef.current || !openRef.current) return;
    closingRef.current = true;
    openRef.current = false;
    const v = draftRef.current;
    const p = propStrRef.current;
    setOpen(false);
    if (v !== p) onCommit(v);
    requestAnimationFrame(() => {
      closingRef.current = false;
    });
  }, [onCommit]);

  useLayoutEffect(() => {
    if (!open) return;
    const ta = textareaRef.current;
    if (!ta) return;
    ta.focus();
    const len = ta.value.length;
    ta.setSelectionRange(len, len);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        finish();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, finish]);

  const onScrimPointerDown = useCallback(() => {
    finish();
  }, [finish]);

  const onTextareaBlur = useCallback(() => {
    requestAnimationFrame(() => {
      if (closingRef.current || !openRef.current) return;
      const ae = document.activeElement;
      if (panelRef.current?.contains(ae)) return;
      finish();
    });
  }, [finish]);

  const shadow = theme.mode === 'dark' ? '0 12px 40px rgba(0,0,0,0.45)' : '0 12px 40px rgba(0,0,0,0.14)';

  return (
    <>
      <input
        ref={anchorRef}
        type="text"
        readOnly
        className="admin-guest-portal__dietary-popout-anchor"
        data-dietary-expanded={open ? 'true' : 'false'}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        value={propStr}
        style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, cursor: 'pointer' }}
        onFocus={() => {
          if (openRef.current) return;
          setDraft(propStr);
          setOpen(true);
        }}
      />
      {open &&
        createPortal(
          <>
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 19998,
                background: theme.modalOverlay,
              }}
              aria-hidden
              onPointerDown={onScrimPointerDown}
            />
            <div
              ref={panelRef}
              className="admin-guest-dietary-editor"
              role="dialog"
              aria-label={ariaLabel}
              style={{
                position: 'fixed',
                zIndex: 19999,
                left: box.left,
                top: box.top,
                width: box.width,
                maxHeight: box.maxH,
                display: 'flex',
                flexDirection: 'column',
                padding: 10,
                boxSizing: 'border-box',
                fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
                background: theme.bgPanel,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                boxShadow: shadow,
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={onTextareaBlur}
                aria-label={ariaLabel}
                style={{
                  flex: 1,
                  minHeight: 96,
                  maxHeight: Math.max(96, box.maxH - 44),
                  width: '100%',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  fontSize: 13,
                  lineHeight: 1.45,
                  fontFamily: 'inherit',
                  color: theme.text,
                  background: theme.bgInput,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  padding: '8px 10px',
                  margin: 0,
                }}
              />
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

/**
 * “Invite link sent” toggles `patchGuest` → heavy parent work (full registry memos). Local checked state updates
 * in the same click frame; `onCommit` runs on the next microtask so the checkbox paint is not blocked by `setGuests`.
 */
function AdminGuestInviteSentCheckbox({
  guestId,
  checked,
  onCommit,
  title,
  'aria-label': ariaLabel,
}) {
  const propChecked = checked === true;
  const [localChecked, setLocalChecked] = useState(propChecked);

  useLayoutEffect(() => {
    setLocalChecked(propChecked);
  }, [guestId, propChecked]);

  return (
    <input
      type="checkbox"
      className="admin-guest-portal__invite-link-sent"
      checked={localChecked}
      title={title}
      aria-label={ariaLabel}
      onChange={(e) => {
        const v = e.target.checked;
        setLocalChecked(v);
        queueMicrotask(() => onCommit(v));
      }}
    />
  );
}

const ADMIN_CUSTOM_TAG_PRESETS_KEY = 'weddingAdminCustomTagPresetsV1';

function readAdminCustomTagPresets() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(ADMIN_CUSTOM_TAG_PRESETS_KEY);
    if (!raw) return [];
    const a = JSON.parse(raw);
    return Array.isArray(a) ? a.map((x) => String(x).trim()).filter(Boolean).slice(0, 40) : [];
  } catch {
    return [];
  }
}

function writeAdminCustomTagPresets(tags) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(ADMIN_CUSTOM_TAG_PRESETS_KEY, JSON.stringify(tags.slice(0, 40)));
  } catch {
    /* ignore */
  }
}

/** Build stored tag label: optional emoji + name (same shape as DEFAULT_TAG_PRESETS). */
function buildAdminPresetLabel(emojiRaw, textRaw) {
  const e = String(emojiRaw || '').trim();
  const t = String(textRaw || '').trim();
  if (!t) return '';
  const combined = e ? `${e} ${t}` : t;
  return combined.slice(0, 64);
}

const GUEST_SIDE_CYCLE = [GUEST_SIDE.UNSPECIFIED, GUEST_SIDE.BEN, GUEST_SIDE.MEG, GUEST_SIDE.JOINT];

function nextGuestSide(current) {
  const raw = current ?? GUEST_SIDE.UNSPECIFIED;
  const i = GUEST_SIDE_CYCLE.indexOf(raw);
  const idx = i === -1 ? 0 : i;
  return GUEST_SIDE_CYCLE[(idx + 1) % GUEST_SIDE_CYCLE.length];
}

/** Compact “who” bubble: B / M / + / unset. */
function guestSideBubble(side, theme) {
  const s = side ?? GUEST_SIDE.UNSPECIFIED;
  const dark = theme.mode === 'dark';
  if (s === GUEST_SIDE.BEN) {
    return {
      char: 'B',
      fg: dark ? '#93c5fd' : '#1e40af',
      bg: dark ? 'rgba(147, 197, 253, 0.2)' : 'rgba(30, 64, 175, 0.12)',
      border: dark ? 'rgba(147, 197, 253, 0.4)' : 'rgba(30, 64, 175, 0.28)',
      title: "Ben's guest — click to cycle",
      label: "Ben's guest",
    };
  }
  if (s === GUEST_SIDE.MEG) {
    return {
      char: 'M',
      fg: dark ? '#f9a8d4' : '#9d174d',
      bg: dark ? 'rgba(244, 114, 182, 0.18)' : 'rgba(157, 23, 77, 0.11)',
      border: dark ? 'rgba(249, 168, 212, 0.42)' : 'rgba(157, 23, 77, 0.24)',
      title: "Meg's guest — click to cycle",
      label: "Meg's guest",
    };
  }
  if (s === GUEST_SIDE.JOINT) {
    return {
      char: '+',
      fg: dark ? '#c4b5fd' : '#5b21b6',
      bg: dark ? 'rgba(196, 181, 253, 0.2)' : 'rgba(91, 33, 182, 0.11)',
      border: dark ? 'rgba(196, 181, 253, 0.38)' : 'rgba(91, 33, 182, 0.22)',
      title: 'Joint — click to cycle',
      label: 'Joint guest',
    };
  }
  return {
    unset: true,
    border: dark ? 'rgba(161, 161, 170, 0.28)' : 'rgba(161, 161, 170, 0.4)',
    bg: 'transparent',
    title: 'Not set — click to set (Ben → Meg → Joint → clear)',
    label: 'Whose guest not set',
  };
}

/** Muted tint for name fields from guest side (Ben / Meg / Joint). */
function guestSideNameStyle(side, theme) {
  const dark = theme.mode === 'dark';
  const s = side ?? GUEST_SIDE.UNSPECIFIED;
  if (s === GUEST_SIDE.BEN) {
    return { color: dark ? 'rgba(147, 197, 253, 0.82)' : 'rgba(30, 64, 175, 0.78)' };
  }
  if (s === GUEST_SIDE.MEG) {
    return { color: dark ? 'rgba(249, 168, 212, 0.82)' : 'rgba(157, 23, 77, 0.75)' };
  }
  if (s === GUEST_SIDE.JOINT) {
    return { color: dark ? 'rgba(196, 181, 253, 0.82)' : 'rgba(91, 33, 182, 0.72)' };
  }
  return { color: theme.text };
}

function IconSearch({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="11" cy="11" r="7" stroke={color} strokeWidth="2" />
      <path d="M20 20l-4-4" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function siteVisibilityFlags(visibility) {
  const penicuik = visibility === SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK;
  const ceremony = penicuik || visibility === SITE_VISIBILITY.CEREMONY_RECEPTION;
  return { ceremony, penicuik };
}

function siteVisibilityFromFlags(ceremony, penicuik) {
  if (!ceremony) return SITE_VISIBILITY.RECEPTION_ONLY;
  if (penicuik) return SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK;
  return SITE_VISIBILITY.CEREMONY_RECEPTION;
}

/** @typedef {'firstName' | 'lastName' | 'adminRsvp' | 'tags' | 'who' | 'ceremony' | 'penicuik' | 'email' | 'phone' | 'group' | 'accommodation' | 'dietary' | 'address' | 'rsvpUpdatedAt'} GuestListSortKey */

const ADMIN_RSVP_SELECT_ORDER = [
  ADMIN_RSVP.NOT_INVITED,
  ADMIN_RSVP.DECLINED,
  ADMIN_RSVP.TENTATIVE,
  ADMIN_RSVP.VERBAL_YES,
  ADMIN_RSVP.CONFIRMED,
];

/** Max venue / list capacity for the invited-guest capacity donut. */
const WEDDING_MAX_GUEST_CAPACITY = 140;

function countRsvpDashboardSlices(guests) {
  let yes = 0;
  let tentative = 0;
  let no = 0;
  let confirmed = 0;
  let notInvited = 0;
  for (const g of guests) {
    const s = g.adminRsvpStatus;
    if (s === ADMIN_RSVP.VERBAL_YES) yes += 1;
    else if (s === ADMIN_RSVP.TENTATIVE) tentative += 1;
    else if (s === ADMIN_RSVP.DECLINED) no += 1;
    else if (s === ADMIN_RSVP.CONFIRMED) confirmed += 1;
    else if (s === ADMIN_RSVP.NOT_INVITED) notInvited += 1;
    else tentative += 1;
  }
  return { yes, tentative, no, confirmed, notInvited };
}

/** Ben / Meg / Joint / unset among guests counted as “attending” in the capacity donut (confirmed + verbal yes + tentative). */
function countAttendingByGuestSide(guests) {
  let ben = 0;
  let meg = 0;
  let joint = 0;
  let unspecified = 0;
  for (const g of guests) {
    const rs = g.adminRsvpStatus;
    if (
      rs !== ADMIN_RSVP.CONFIRMED &&
      rs !== ADMIN_RSVP.VERBAL_YES &&
      rs !== ADMIN_RSVP.TENTATIVE
    ) {
      continue;
    }
    const side = g.guestSide ?? GUEST_SIDE.UNSPECIFIED;
    if (side === GUEST_SIDE.BEN) ben += 1;
    else if (side === GUEST_SIDE.MEG) meg += 1;
    else if (side === GUEST_SIDE.JOINT) joint += 1;
    else unspecified += 1;
  }
  const total = ben + meg + joint + unspecified;
  return { ben, meg, joint, unspecified, total };
}

/** Headcount weight per registry row for coach totals (Typeform “party seats” or default 1). */
function coachSeatPlanningWeight(g) {
  const n = g.coachSeatCount;
  if (typeof n === 'number' && n > 0) return Math.min(50, Math.floor(n));
  return 1;
}

/**
 * RSVPd + verbal yes only (excludes tentative) so coach counts reflect relatively firm bookings.
 */
function guestCountsForCoachSeatTotals(g) {
  const s = g.adminRsvpStatus;
  return s === ADMIN_RSVP.CONFIRMED || s === ADMIN_RSVP.VERBAL_YES;
}

function computeCoachSeatTotals(guests) {
  let inbound230 = 0;
  let out1030 = 0;
  let out1230 = 0;
  for (const g of guests) {
    if (!guestCountsForCoachSeatTotals(g)) continue;
    const w = coachSeatPlanningWeight(g);
    if (g.coachIn === true) inbound230 += w;
    if (g.coachOut === '10:30') out1030 += w;
    if (g.coachOut === '12:30') out1230 += w;
  }
  return { inbound230, out1030, out1230 };
}

/** Party-weighted seats interested in 29 Dec train (Typeform / admin), same eligibility as coach totals. */
function computeTrainDec29InterestSeatTotal(guests) {
  let n = 0;
  for (const g of guests) {
    if (!guestCountsForCoachSeatTotals(g)) continue;
    if (g.trainTravelDec29 !== true) continue;
    n += coachSeatPlanningWeight(g);
  }
  return n;
}

/**
 * Apply partial updates to one guest row (keeps single-row and multi-row patch behaviour identical).
 */
/** Updates allowed to apply to all ticked rows when the edited row is part of a multi-selection. */
const ADMIN_BULK_DROPDOWN_KEYS = new Set(['adminRsvpStatus']);

function mergeGuestPatch(g, updates) {
  const u = { ...updates };
  if (u.email !== undefined) u.email = normalizeEmail(u.email);
  if (u.plusOnes !== undefined) u.plusOnes = normalizePlusOnes(u.plusOnes);
  if (u.coachSeatCount !== undefined) {
    const cs = normalizeCoachSeatCount(u.coachSeatCount);
    if (cs === undefined) delete u.coachSeatCount;
    else u.coachSeatCount = cs;
  }
  const clearTrainDec29 =
    Object.prototype.hasOwnProperty.call(updates, 'trainTravelDec29') &&
    updates.trainTravelDec29 === null;
  if (u.trainTravelDec29 !== undefined) {
    if (u.trainTravelDec29 === null) delete u.trainTravelDec29;
    else u.trainTravelDec29 = Boolean(u.trainTravelDec29);
  }
  if (u.inviteUrlSent !== undefined) {
    u.inviteUrlSent = Boolean(u.inviteUrlSent);
  }
  if (u.tags !== undefined) u.tags = normalizeTags(u.tags);
  const merged = { ...g, ...u };
  if (clearTrainDec29) delete merged.trainTravelDec29;
  if (u.adminRsvpStatus !== undefined) {
    const { formRsvp: fr, verbalRsvp: vr } = formAndVerbalFromAdminRsvp(u.adminRsvpStatus);
    merged.adminRsvpStatus = u.adminRsvpStatus;
    merged.formRsvp = fr;
    merged.verbalRsvp = vr;
    merged.rsvpStatus = computeRsvpStatusFromForm(fr);
    if (fr !== g.formRsvp) {
      merged.rsvpUpdatedAt = fr === FORM_RSVP.PENDING ? g.rsvpUpdatedAt : new Date().toISOString();
    }
  }
  if (u.formRsvp !== undefined && u.adminRsvpStatus === undefined) {
    merged.rsvpStatus = computeRsvpStatusFromForm(u.formRsvp);
    if (u.formRsvp !== g.formRsvp) merged.rsvpUpdatedAt = new Date().toISOString();
    if (u.formRsvp === FORM_RSVP.YES) merged.adminRsvpStatus = ADMIN_RSVP.CONFIRMED;
    else if (u.formRsvp === FORM_RSVP.NO) merged.adminRsvpStatus = ADMIN_RSVP.DECLINED;
  }
  return merged;
}

/** Capacity donut + breakdown + ceremony / to-invite stats (guests tab toolbar). */
function AdminRsvpOverviewStrip({
  compact = false,
  invitedGuests,
  trainConfirmedGuestCount,
  ceremonyGuestCount,
  theme,
  filterCeremony = false,
  onToggleCeremonyFilter,
  filterTrainInterested = false,
  onToggleTrainInterestedFilter,
  filterTrainConfirmed = false,
  onToggleTrainConfirmedFilter,
  filterRsvpConfirmed = false,
  onToggleRsvpConfirmedFilter,
}) {
  const maxCap = WEDDING_MAX_GUEST_CAPACITY;
  const { inbound230, out1030, out1230 } = useMemo(
    () => computeCoachSeatTotals(invitedGuests),
    [invitedGuests],
  );
  const trainDec29Seats = useMemo(
    () => computeTrainDec29InterestSeatTotal(invitedGuests),
    [invitedGuests],
  );
  const sideAttending = useMemo(() => countAttendingByGuestSide(invitedGuests), [invitedGuests]);
  const { yes, tentative, confirmed, notInvited, no: declined } = countRsvpDashboardSlices(invitedGuests);
  const filled = yes + confirmed + tentative;
  const gapCount = Math.max(0, maxCap - filled);
  const overCapacity = filled > maxCap;

  if (compact) {
    return <AdminCompactOverview
      maxCapacity={maxCap}
      rsvp={{ yes, tentative, confirmed, notInvited, no: declined }}
      coachSeats={{ inbound230, out1030, out1230 }}
      sideAttending={sideAttending}
      trainInterested={trainDec29Seats}
      trainConfirmed={trainConfirmedGuestCount}
      ceremonyCount={ceremonyGuestCount}
      theme={theme}
      filterCeremony={filterCeremony} onToggleCeremonyFilter={onToggleCeremonyFilter}
      filterTrainInterested={filterTrainInterested} onToggleTrainInterestedFilter={onToggleTrainInterestedFilter}
      filterTrainConfirmed={filterTrainConfirmed} onToggleTrainConfirmedFilter={onToggleTrainConfirmedFilter}
      filterRsvpConfirmed={filterRsvpConfirmed} onToggleRsvpConfirmedFilter={onToggleRsvpConfirmedFilter}
    />;
  }

  const cVerbalYes =
    theme.mode === 'dark' ? 'rgba(74, 222, 128, 0.48)' : 'rgba(22, 163, 74, 0.42)';
  const cGap =
    theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(24, 24, 27, 0.06)';

  let gradient =
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(24,24,27,0.06)';
  if (filled > 0 || gapCount > 0) {
    const ranges = [];
    let acc = 0;
    const pushRange = (c, pct) => {
      if (pct <= 0) return;
      const start = acc;
      acc += pct;
      ranges.push({ c, start, end: acc });
    };

    if (overCapacity) {
      const t = filled;
      pushRange(theme.rsvp.confirmed, (confirmed / t) * 100);
      pushRange(cVerbalYes, (yes / t) * 100);
      pushRange(theme.rsvp.pending, (tentative / t) * 100);
    } else {
      pushRange(theme.rsvp.confirmed, (confirmed / maxCap) * 100);
      pushRange(cVerbalYes, (yes / maxCap) * 100);
      pushRange(theme.rsvp.pending, (tentative / maxCap) * 100);
      pushRange(cGap, (gapCount / maxCap) * 100);
    }
    if (ranges.length > 0) {
      ranges[ranges.length - 1].end = 100;
    }
    if (ranges.length > 0) {
      gradient = `conic-gradient(from 0deg, ${ranges.map(({ c, start, end }) => `${c} ${start}% ${end}%`).join(', ')})`;
    }
  }

  const ariaBreakdown = overCapacity
    ? `Over capacity ${maxCap}. RSVPd ${confirmed}, verbal yes ${yes}, tentative ${tentative}, declined ${declined}, total attending ${filled}.`
    : `Capacity ${maxCap}. RSVPd ${confirmed}, verbal yes ${yes}, tentative ${tentative}, declined ${declined}, attending ${filled}, space left ${gapCount}, not yet invited ${notInvited}.`;

  const cSideBen =
    theme.mode === 'dark' ? 'rgba(147, 197, 253, 0.92)' : 'rgba(30, 64, 175, 0.82)';
  const cSideMeg =
    theme.mode === 'dark' ? 'rgba(249, 168, 212, 0.88)' : 'rgba(157, 23, 77, 0.78)';
  const cSideJoint =
    theme.mode === 'dark' ? 'rgba(196, 181, 253, 0.9)' : 'rgba(91, 33, 182, 0.78)';
  const cSideUnspec =
    theme.mode === 'dark' ? 'rgba(161, 161, 170, 0.5)' : 'rgba(82, 82, 91, 0.38)';

  const { ben: sideBen, meg: sideMeg, joint: sideJoint, unspecified: sideUnspec, total: sideTotal } =
    sideAttending;
  let sideGradient =
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(24,24,27,0.06)';
  if (sideTotal > 0) {
    const ranges = [];
    let acc = 0;
    const pushSlice = (c, n) => {
      if (n <= 0) return;
      const pct = (n / sideTotal) * 100;
      const start = acc;
      acc += pct;
      ranges.push({ c, start, end: acc });
    };
    pushSlice(cSideBen, sideBen);
    pushSlice(cSideMeg, sideMeg);
    pushSlice(cSideJoint, sideJoint);
    pushSlice(cSideUnspec, sideUnspec);
    if (ranges.length > 0) {
      ranges[ranges.length - 1].end = 100;
      sideGradient = `conic-gradient(from 0deg, ${ranges.map(({ c, start, end }) => `${c} ${start}% ${end}%`).join(', ')})`;
    }
  }

  const ariaSideBreakdown =
    sideTotal > 0
      ? `Attending guest balance. Ben ${sideBen} (${Math.round((sideBen / sideTotal) * 100)} percent), Meg ${sideMeg} (${Math.round((sideMeg / sideTotal) * 100)} percent), joint ${sideJoint} (${Math.round((sideJoint / sideTotal) * 100)} percent), side not set ${sideUnspec} (${Math.round((sideUnspec / sideTotal) * 100)} percent).`
      : 'No attending guests to show balance.';

  const rule = theme.mode === 'dark' ? 'rgba(244, 244, 245, 0.12)' : 'rgba(24, 24, 27, 0.08)';

  const donutPx = 192;
  /** Supporting breakdown: compact labels, muted tabular counts (does not compete with donut hero). */
  const capacityLegendRow = (
    dotColor,
    primary,
    secondary,
    count,
    { numberPx = 14, numberWeight = 600, numberColor, pctDenominator } = {},
  ) => (
    <div
      key={primary + (secondary ?? '')}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
        columnGap: 12,
        minHeight: secondary ? undefined : 22,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', minWidth: 0 }}>
        <span
          style={{ color: dotColor, fontSize: 8, lineHeight: 1.35, flexShrink: 0, marginTop: 3 }}
          aria-hidden
        >
          ●
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: theme.muted, lineHeight: 1.3 }}>{primary}</div>
          {secondary ? (
            <div
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: theme.mutedLight,
                lineHeight: 1.25,
                marginTop: 1,
              }}
            >
              {secondary}
            </div>
          ) : null}
        </div>
      </div>
      <div
        style={{
          fontSize: numberPx,
          fontWeight: numberWeight,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
          color: numberColor ?? theme.muted,
          letterSpacing: '-0.02em',
        }}
      >
        {pctDenominator != null && pctDenominator > 0 ? (
          <>
            {count}
            <span style={{ fontWeight: 600 }}>{` (${Math.round((count / pctDenominator) * 100)}%)`}</span>
          </>
        ) : (
          count
        )}
      </div>
    </div>
  );

  const coachChip = (title, subtitle, count, accentRgb, chipOptions = null) => {
    const { onClick, pressed, ariaLabel, tooltip } = chipOptions || {};
    const baseBox = {
      borderRadius: 8,
      padding: '5px 7px',
      background:
        theme.mode === 'dark' ? `rgba(${accentRgb}, 0.1)` : `rgba(${accentRgb}, 0.08)`,
      border:
        theme.mode === 'dark'
          ? `1px solid rgba(${accentRgb}, 0.18)`
          : `1px solid rgba(${accentRgb}, 0.14)`,
      minWidth: 0,
    };
    if (pressed) {
      baseBox.boxShadow =
        theme.mode === 'dark'
          ? `inset 0 0 0 1px rgba(${accentRgb}, 0.45)`
          : `inset 0 0 0 2px rgba(${accentRgb}, 0.35)`;
      baseBox.background =
        theme.mode === 'dark' ? `rgba(${accentRgb}, 0.16)` : `rgba(${accentRgb}, 0.12)`;
    }
    const inner = (
      <>
        <div
          style={{
            fontSize: 7,
            fontWeight: 700,
            color: theme.mutedLight,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            marginBottom: 2,
            lineHeight: 1.15,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
            color: theme.text,
            letterSpacing: '-0.03em',
            lineHeight: 1,
          }}
        >
          {count}
        </div>
        {subtitle ? (
          <div
            style={{
              fontSize: 8,
              fontWeight: 500,
              color: theme.muted,
              marginTop: 2,
              lineHeight: 1.2,
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </>
    );
    if (onClick) {
      return (
        <button
          key={title}
          type="button"
          onClick={onClick}
          title={tooltip}
          aria-label={ariaLabel}
          aria-pressed={pressed}
          className="admin-guest-portal__overview-chip-btn"
          style={{
            ...baseBox,
            display: 'block',
            width: '100%',
            cursor: 'pointer',
            textAlign: 'left',
            font: 'inherit',
            boxSizing: 'border-box',
          }}
        >
          {inner}
        </button>
      );
    }
    return (
      <div key={title} style={baseBox}>
        {inner}
      </div>
    );
  };

  const sideDonutPx = 156;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns:
          'minmax(0, auto) minmax(100px, 1fr) minmax(200px, auto) minmax(188px, auto) auto',
        alignItems: 'stretch',
        columnGap: 24,
        rowGap: 12,
        padding: '8px 4px 8px 0',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifySelf: 'start',
          justifyContent: 'center',
        }}
      >
        <div
          style={{ position: 'relative', width: donutPx, height: donutPx }}
          aria-label={ariaBreakdown}
          role="img"
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: gradient,
              boxSizing: 'border-box',
              boxShadow:
                theme.mode === 'dark'
                  ? 'inset 0 0 0 1px rgba(255,255,255,0.06)'
                  : 'inset 0 0 0 1px rgba(24,24,27,0.06)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: '54%',
              height: '54%',
              borderRadius: '50%',
              background: theme.bgMuted,
              zIndex: 1,
              boxShadow:
                theme.mode === 'dark'
                  ? '0 1px 0 rgba(255,255,255,0.04)'
                  : '0 1px 0 rgba(255,255,255,0.85)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              textAlign: 'center',
              pointerEvents: 'none',
              width: '88%',
            }}
          >
            <div
              style={{
                fontSize: 42,
                fontWeight: 800,
                lineHeight: 0.95,
                color: theme.text,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.04em',
              }}
            >
              {filled}
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: theme.muted,
                marginTop: 6,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              Attending
            </div>
          </div>
        </div>
        <button
          type="button"
          className="admin-guest-portal__donut-confirmed-filter"
          onClick={() => onToggleRsvpConfirmedFilter?.()}
          aria-pressed={filterRsvpConfirmed}
          title="Filter the guest list to RSVP confirmed (form) only. Click again to clear."
          style={{
            marginTop: 10,
            fontSize: 13,
            fontWeight: 800,
            fontFamily: 'inherit',
            color: theme.rsvp.confirmed,
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'center',
            lineHeight: 1.4,
            maxWidth: donutPx + 40,
            cursor: 'pointer',
            border: filterRsvpConfirmed
              ? `1px solid ${theme.mode === 'dark' ? 'rgba(74, 222, 128, 0.45)' : 'rgba(22, 163, 74, 0.35)'}`
              : '1px solid transparent',
            borderRadius: 10,
            padding: '5px 10px',
            background: filterRsvpConfirmed
              ? theme.mode === 'dark'
                ? 'rgba(74, 222, 128, 0.12)'
                : 'rgba(22, 163, 74, 0.1)'
              : 'transparent',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {confirmed} confirmed
        </button>
        {overCapacity ? (
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: theme.danger,
              marginTop: 8,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Over capacity
          </div>
        ) : null}
      </div>

      <div
        style={{
          minWidth: 0,
          paddingLeft: 22,
          marginLeft: 2,
          borderLeft: `1px solid ${rule}`,
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          opacity: 0.92,
        }}
      >
        <div
          style={{
            fontSize: 9,
            fontWeight: 600,
            color: theme.mutedLight,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: 10,
          }}
        >
          Capacity · {maxCap}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxWidth: 260, minWidth: 0 }}>
          {capacityLegendRow(cVerbalYes, 'Yes', 'Verbal', yes)}
          {capacityLegendRow(theme.rsvp.pending, 'Pending', 'Tentative', tentative)}
          {capacityLegendRow(theme.rsvp.confirmed, 'Confirmed', 'RSVPd', confirmed)}
          <div
            role="separator"
            style={{
              height: 1,
              background: rule,
              margin: '5px 0 3px',
              alignSelf: 'stretch',
              opacity: 0.85,
            }}
          />
          {capacityLegendRow(theme.rsvp.declined, 'Declined', null, declined, {
            numberPx: 12,
            numberWeight: 600,
          })}
          {capacityLegendRow(cGap, 'Space left', null, gapCount)}
          {capacityLegendRow(theme.rsvp.notSent, 'Not yet invited', null, notInvited)}
        </div>
      </div>

      <div
        style={{
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '14px 18px',
          paddingLeft: 20,
          marginLeft: 2,
          borderLeft: `1px solid ${rule}`,
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <div
            style={{ width: sideDonutPx, height: sideDonutPx }}
            aria-label={ariaSideBreakdown}
            role="img"
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                background: sideGradient,
                boxSizing: 'border-box',
                boxShadow:
                  theme.mode === 'dark'
                    ? 'inset 0 0 0 1px rgba(255,255,255,0.06)'
                    : 'inset 0 0 0 1px rgba(24,24,27,0.06)',
              }}
            />
          </div>
        </div>
        <div
          style={{
            flex: '1 1 160px',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 7,
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 600,
              color: theme.mutedLight,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: 2,
            }}
          >
            Guest balance
          </div>
          <div style={{ fontSize: 9, fontWeight: 500, color: theme.muted, lineHeight: 1.35, marginBottom: 4 }}>
            Matches centre donut headcount: confirmed + verbal yes + tentative.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 220, minWidth: 0 }}>
            {capacityLegendRow(cSideBen, "Ben's guests", null, sideBen, {
              numberPx: 12,
              numberWeight: 600,
              pctDenominator: sideTotal > 0 ? sideTotal : undefined,
            })}
            {capacityLegendRow(cSideMeg, "Meg's guests", null, sideMeg, {
              numberPx: 12,
              numberWeight: 600,
              pctDenominator: sideTotal > 0 ? sideTotal : undefined,
            })}
            {capacityLegendRow(cSideJoint, 'Joint', 'Both / shared', sideJoint, {
              numberPx: 12,
              numberWeight: 600,
              pctDenominator: sideTotal > 0 ? sideTotal : undefined,
            })}
            {capacityLegendRow(cSideUnspec, 'Side not set', null, sideUnspec, {
              numberPx: 12,
              numberWeight: 600,
              pctDenominator: sideTotal > 0 ? sideTotal : undefined,
            })}
          </div>
        </div>
      </div>

      <div
        style={{
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          paddingLeft: 20,
          marginLeft: 2,
          borderLeft: `1px solid ${rule}`,
          minWidth: 0,
        }}
      >
        <div
          style={{
            marginBottom: 6,
          }}
        >
          <span
            style={{
              fontSize: 8,
              fontWeight: 700,
              color: theme.mutedLight,
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
            }}
          >
            Coach seats
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 5,
            width: '100%',
            maxWidth: 300,
          }}
        >
          {coachChip(
            'To estate',
            '2:30 · Edinburgh pickup',
            inbound230,
            theme.mode === 'dark' ? '96, 165, 250' : '37, 99, 235',
          )}
          {coachChip(
            'Return',
            '22:30 bus',
            out1030,
            theme.mode === 'dark' ? '167, 139, 250' : '109, 40, 217',
          )}
          {coachChip(
            'Late return',
            '00:30 bus',
            out1230,
            theme.mode === 'dark' ? '52, 211, 153' : '5, 150, 105',
          )}
        </div>
        <div style={{ marginTop: 10 }}>
          <span
            style={{
              fontSize: 8,
              fontWeight: 700,
              color: theme.mutedLight,
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
            }}
          >
            Train
          </span>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 5,
              width: '100%',
              maxWidth: 300,
              marginTop: 5,
            }}
          >
            {coachChip(
              'Interested',
              '',
              trainDec29Seats,
              theme.mode === 'dark' ? '45, 212, 191' : '13, 148, 136',
              onToggleTrainInterestedFilter
                ? {
                    onClick: onToggleTrainInterestedFilter,
                    pressed: filterTrainInterested,
                    ariaLabel: `Train interested: ${trainDec29Seats}`,
                    tooltip: 'Show guests who chose Yes for train. Tap again to clear.',
                  }
                : null,
            )}
            {coachChip(
              'Confirmed',
              '',
              trainConfirmedGuestCount,
              theme.mode === 'dark' ? '74, 222, 128' : '22, 163, 74',
              {
                onClick: onToggleTrainConfirmedFilter,
                pressed: filterTrainConfirmed,
                ariaLabel: `Train confirmed: ${trainConfirmedGuestCount}`,
                tooltip: 'Show guests with a numbered train booking tag (train - X). Tap again to clear.',
              },
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          justifySelf: 'end',
          alignSelf: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '6px 28px',
          paddingLeft: 20,
          marginLeft: 2,
          borderLeft: `1px solid ${rule}`,
          boxSizing: 'border-box',
        }}
      >
        <button
          type="button"
          onClick={() => onToggleCeremonyFilter?.()}
          aria-pressed={filterCeremony}
          title="Filter the guest list to ceremony guests only (same as SITE → Ceremony). Click again to clear."
          className="admin-guest-portal__ceremony-callout-filter"
          style={{
            textAlign: 'center',
            minWidth: 88,
            padding: '12px 18px',
            borderRadius: 14,
            cursor: onToggleCeremonyFilter ? 'pointer' : 'default',
            font: 'inherit',
            background: filterCeremony ? ceremonyGuestRowTint(theme) : theme.indigoTint,
            border: filterCeremony
              ? `1px solid ${ceremonyGuestStarColor(theme)}`
              : theme.mode === 'dark'
                ? '1px solid rgba(165, 180, 252, 0.28)'
                : '1px solid rgba(79, 70, 229, 0.2)',
            boxShadow:
              theme.mode === 'dark'
                ? '0 1px 0 rgba(255,255,255,0.04)'
                : '0 2px 8px rgba(79, 70, 229, 0.08)',
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: filterCeremony ? ceremonyGuestStarColor(theme) : theme.indigo,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            Ceremony
          </div>
          <div
            style={{
              fontSize: 36,
              fontWeight: 800,
              lineHeight: 0.95,
              color: theme.text,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.04em',
            }}
          >
            {ceremonyGuestCount}
          </div>
        </button>
      </div>
    </div>
  );
}

/** For column sort: missing/invalid timestamps sort before all real times when ascending. */
function rsvpUpdatedAtSortValue(g) {
  const raw = g.rsvpUpdatedAt;
  if (raw == null || typeof raw !== 'string') return -1;
  const t = Date.parse(raw);
  return Number.isNaN(t) ? -1 : t;
}

/** Human-readable age of `rsvpUpdatedAt` ISO string; updates when `nowMs` changes. */
function formatRsvpUpdatedAgo(iso, nowMs) {
  if (iso == null || typeof iso !== 'string' || !iso.trim()) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const sec = Math.max(0, Math.floor((nowMs - t) / 1000));
  if (sec < 45) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86400 * 60) return `${Math.floor(sec / 86400)}d ago`;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function useTickMs(intervalMs = 60000) {
  const [t, setT] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setT(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return t;
}

/** @param {GuestListSortKey} sortKey */
function compareGuestRowsForSort(a, b, sortKey, asc) {
  const dir = asc ? 1 : -1;
  let cmp = 0;
  switch (sortKey) {
    case 'firstName':
      cmp = (a.firstName || '')
        .trim()
        .localeCompare((b.firstName || '').trim(), undefined, { sensitivity: 'base' });
      break;
    case 'lastName':
      cmp = (a.lastName || '')
        .trim()
        .localeCompare((b.lastName || '').trim(), undefined, { sensitivity: 'base' });
      break;
    case 'adminRsvp': {
      const rank = (s) => {
        const i = ADMIN_RSVP_SELECT_ORDER.indexOf(s);
        return i === -1 ? ADMIN_RSVP_SELECT_ORDER.length : i;
      };
      cmp = rank(a.adminRsvpStatus) - rank(b.adminRsvpStatus);
      break;
    }
    case 'email':
      cmp = (a.email || '')
        .trim()
        .toLowerCase()
        .localeCompare((b.email || '').trim().toLowerCase());
      break;
    case 'phone':
      cmp = (a.phone || '').trim().localeCompare((b.phone || '').trim(), undefined, { numeric: true });
      break;
    case 'tags': {
      const pack = (g) =>
        [...(g.tags || [])]
          .map((t) => String(t).toLowerCase())
          .sort()
          .join('\0');
      cmp = pack(a).localeCompare(pack(b));
      break;
    }
    case 'who': {
      const rank = (side) => {
        const v = side ?? GUEST_SIDE.UNSPECIFIED;
        if (v === GUEST_SIDE.BEN) return 1;
        if (v === GUEST_SIDE.MEG) return 2;
        if (v === GUEST_SIDE.JOINT) return 3;
        return 0;
      };
      cmp = rank(a.guestSide) - rank(b.guestSide);
      break;
    }
    case 'ceremony': {
      const ca = siteVisibilityFlags(a.siteVisibility).ceremony;
      const cb = siteVisibilityFlags(b.siteVisibility).ceremony;
      cmp = ca === cb ? 0 : ca ? 1 : -1;
      break;
    }
    case 'penicuik': {
      const pa = siteVisibilityFlags(a.siteVisibility).penicuik;
      const pb = siteVisibilityFlags(b.siteVisibility).penicuik;
      cmp = pa === pb ? 0 : pa ? 1 : -1;
      break;
    }
    case 'group':
      cmp = (a.group || '').trim().localeCompare((b.group || '').trim(), undefined, { sensitivity: 'base' });
      break;
    case 'accommodation':
      cmp = (a.accommodation || '')
        .trim()
        .localeCompare((b.accommodation || '').trim(), undefined, { sensitivity: 'base' });
      break;
    case 'dietary':
      cmp = (a.dietary || '').trim().localeCompare((b.dietary || '').trim(), undefined, { sensitivity: 'base' });
      break;
    case 'address':
      cmp = (a.address || '').trim().localeCompare((b.address || '').trim(), undefined, { sensitivity: 'base' });
      break;
    case 'rsvpUpdatedAt':
      cmp = rsvpUpdatedAtSortValue(a) - rsvpUpdatedAtSortValue(b);
      break;
    default:
      return 0;
  }
  if (cmp !== 0) return dir * cmp;
  const sa = a.sortOrder ?? 0;
  const sb = b.sortOrder ?? 0;
  if (sa !== sb) return sa - sb;
  const na = `${a.firstName || ''} ${a.lastName || ''}`.trim();
  const nb = `${b.firstName || ''} ${b.lastName || ''}`.trim();
  return na.localeCompare(nb, undefined, { sensitivity: 'base' });
}

/** Muted outline colour for ceremony / Penicuik icons when off. */
function siteToggleIconOffColor(theme) {
  return theme.mode === 'dark' ? 'rgba(244, 244, 245, 0.16)' : 'rgba(24, 24, 27, 0.09)';
}

/** Amber / gold for ceremony guest rows, star, and ceremony column sort (not admin accent / focus). */
function ceremonyGuestStarColor(theme) {
  return theme.mode === 'dark' ? '#fbbf24' : '#b45309';
}

function ceremonyGuestRowTint(theme) {
  return theme.mode === 'dark' ? 'rgba(251, 191, 36, 0.07)' : 'rgba(217, 119, 6, 0.055)';
}

/** Semi-transparent green layer on top of base row tint for form-confirmed guests. */
function confirmedGuestRowBackground(theme, baseBackground) {
  const overlay =
    theme.mode === 'dark' ? 'rgba(74, 222, 128, 0.2)' : 'rgba(22, 163, 74, 0.16)';
  return `linear-gradient(${overlay}, ${overlay}), ${baseBackground}`;
}

function parseThemeBgRgb(theme) {
  const h = theme.bg.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function blendRgbaOn(rgb, r, g, b, a) {
  return {
    r: Math.round(rgb.r * (1 - a) + r * a),
    g: Math.round(rgb.g * (1 - a) + g * a),
    b: Math.round(rgb.b * (1 - a) + b * a),
  };
}

function rgbCss(rgb) {
  return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
}

function ceremonyTintRgb(theme) {
  const bg = parseThemeBgRgb(theme);
  return theme.mode === 'dark'
    ? blendRgbaOn(bg, 251, 191, 36, 0.07)
    : blendRgbaOn(bg, 217, 119, 6, 0.055);
}

function receptionTintRgb(theme) {
  const bg = parseThemeBgRgb(theme);
  return theme.mode === 'dark'
    ? blendRgbaOn(bg, 161, 161, 170, 0.05)
    : blendRgbaOn(bg, 24, 24, 27, 0.02);
}

function backupTintRgb(theme) {
  const bg = parseThemeBgRgb(theme);
  return theme.mode === 'dark'
    ? blendRgbaOn(bg, 251, 191, 36, 0.12)
    : blendRgbaOn(bg, 251, 191, 36, 0.09);
}

function confirmedGreenTintRgb(theme, baseRgb) {
  return theme.mode === 'dark'
    ? blendRgbaOn(baseRgb, 74, 222, 128, 0.2)
    : blendRgbaOn(baseRgb, 22, 163, 74, 0.16);
}

/** SF Symbol–style star: outline vs filled (web approximation). */
function SFSymbolStar({ filled, onColor, offColor }) {
  const c = filled ? onColor : offColor;
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      {filled ? (
        <path
          d="M12 2.25l2.67 8.2h8.65l-7 5.08 2.67 8.22L12 17.77l-7 6.98 2.67-8.22-7-5.08h8.65L12 2.25z"
          fill={c}
        />
      ) : (
        <path
          d="M12 2.25l2.67 8.2h8.65l-7 5.08 2.67 8.22L12 17.77l-7 6.98 2.67-8.22-7-5.08h8.65L12 2.25z"
          fill="none"
          stroke={c}
          strokeWidth={1.15}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

/** SF Symbol–style house: outline vs filled (same path as roof + walls + door). */
function SFSymbolHouse({ filled, onColor, offColor }) {
  const c = filled ? onColor : offColor;
  const d = 'M12 4.25L4 10.25V20h5v-7.25h6V20h5V10.25L12 4.25z';
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      {filled ? (
        <path d={d} fill={c} />
      ) : (
        <path
          d={d}
          fill="none"
          stroke={c}
          strokeWidth={1.15}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

function groupKeyNormalized(group) {
  return (group || '').trim().toLowerCase();
}

/** Stable hue 0–359 from group string; null when empty. */
function groupStripeHue(group) {
  const k = groupKeyNormalized(group);
  if (!k) return null;
  let h = 0;
  for (let i = 0; i < k.length; i += 1) h = (h * 31 + k.charCodeAt(i)) >>> 0;
  return h % 360;
}

function groupStripeStyle(group, theme) {
  const hue = groupStripeHue(group);
  if (hue == null) return {};
  if (theme.mode === 'dark') {
    return { background: `hsl(${hue} 42% 40%)` };
  }
  return { background: `hsl(${hue} 42% 72%)` };
}

/** Same hue as `groupStripeStyle`; lightness tuned for readable text on sheet cells. */
function groupLabelColor(group, theme) {
  const hue = groupStripeHue(group);
  if (hue == null) return null;
  if (theme.mode === 'dark') {
    return `hsl(${hue} 48% 74%)`;
  }
  return `hsl(${hue} 40% 32%)`;
}

function guestPickerLines(g) {
  return {
    first: String(g.firstName || '').trim(),
    last: String(g.lastName || '').trim(),
    email: String(g.email || '').trim(),
  };
}

/** Stored group / household label when chosen from a guest: first + last when both exist, else whichever name parts or email. */
function guestHouseholdNameLabel(g) {
  const { first, last, email } = guestPickerLines(g);
  if (first && last) return `${first} ${last}`;
  const single = `${first} ${last}`.trim();
  if (single) return single;
  return email || 'Guest';
}

/** Portal mount for typeahead panels (tags, household group) so they sit above sheet rows and stay clickable. */
const TagSuggestPortalHostContext = createContext(null);
/** Full guest list ref (stable identity); group picker reads `ref.current` so memo’d sheet rows are not forced to re-render on every patch. */
const AdminAllGuestsRefContext = createContext(null);
/** Tag palette ref; updated each render so `GuestTagSuggestInput` reads fresh data without breaking row memo. */
const TagPaletteRefContext = createContext(null);

/** Stable fallbacks so `?? []` does not allocate a new array every render (breaks exhaustive-deps / useMemo). */
const EMPTY_GUEST_ARRAY = [];
const EMPTY_TAG_PALETTE_ENTRIES = [];

/**
 * Household group: only assignable by picking a guest; stored value is that guest’s name label (first + last when both set).
 * `onAssign(label, anchorGuestId)` — anchor is the picked guest so their row can get the same `group` for display/sorting.
 * Suggestions use the admin portal host + fixed positioning so lower table rows do not steal clicks.
 */
function computeSuggestionPanelBox(anchorRect, minWidth, maxHeight) {
  const viewport = window.visualViewport;
  const viewportTop = viewport?.offsetTop || 0;
  const bottom = viewportTop + (viewport?.height || window.innerHeight) - 8;
  const width = Math.min(Math.max(minWidth, anchorRect.width), window.innerWidth - 16);
  const below = bottom - anchorRect.bottom - 4;
  const above = anchorRect.top - viewportTop - 12;
  const openAbove = below < Math.min(maxHeight, 160) && above > below;
  const height = Math.max(44, Math.min(maxHeight, openAbove ? above : below));
  return {
    left: Math.max(8, Math.min(anchorRect.left, window.innerWidth - width - 8)),
    top: openAbove ? Math.max(viewportTop + 8, anchorRect.top - height - 4) : anchorRect.bottom + 4,
    width,
    maxHeight: height,
  };
}

function GroupAssignSuggestInput({ rowGuest, theme, btnBase, onAssign, onClear }) {
  const allGuestsRef = useContext(AdminAllGuestsRefContext);
  const allGuests = allGuestsRef?.current ?? EMPTY_GUEST_ARRAY;
  const groupPortalHost = useContext(TagSuggestPortalHostContext);
  const hasGroup = Boolean((rowGuest.group || '').trim());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 200 });
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const draftRef = useRef(draft);
  const listId = `group-assign-${rowGuest.id}`;
  const panelId = `${listId}-panel`;

  const pickerRows = useMemo(() => {
    return [...allGuests]
      .map((cg) => {
        const { first, last, email } = guestPickerLines(cg);
        const label = guestHouseholdNameLabel(cg);
        const hay = `${first} ${last} ${email}`.toLowerCase().replace(/\s+/g, ' ').trim();
        return { id: cg.id, first, last, email, label, hay, groupValue: label };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [allGuests]);

  const matches = useMemo(() => {
    const q = draft.trim().toLowerCase();
    const filtered = q ? pickerRows.filter((r) => r.hay.includes(q)) : pickerRows;
    return filtered.slice(0, 24);
  }, [pickerRows, draft]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    setHi(0);
  }, [draft]);

  const updatePanelPosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPanelPos(computeSuggestionPanelBox(r, 200, 230));
  }, []);

  useEffect(() => {
    if (!open && !editing) return;
    const close = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
      if (!hasGroup && editing && !draftRef.current.trim()) setEditing(false);
      if (hasGroup && editing && !draftRef.current.trim()) setEditing(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, editing, hasGroup]);

  const commitPick = useCallback(
    (row) => {
      onAssign(row.groupValue, row.id);
      setDraft('');
      setOpen(false);
      setHi(0);
      setEditing(false);
    },
    [onAssign],
  );

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      if (!draft.trim() && !hasGroup) {
        setEditing(false);
        if (e.target instanceof HTMLElement) e.target.blur();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      if (matches.length) setHi((h) => Math.min(h + 1, matches.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (matches.length) setHi((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (matches.length && open) {
        commitPick(matches[Math.min(hi, matches.length - 1)]);
      }
      return;
    }
  };

  const showList = open && matches.length > 0;
  const groupPortalMount =
    groupPortalHost ?? (typeof document !== 'undefined' ? document.body : null);
  const groupPopoverZ = groupPortalHost ? 1 : 2147483000;

  useLayoutEffect(() => {
    if (!showList) return;
    updatePanelPosition();
    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.visualViewport?.addEventListener('resize', onScrollOrResize);
    window.visualViewport?.addEventListener('scroll', onScrollOrResize);
    document.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.visualViewport?.removeEventListener('resize', onScrollOrResize);
      window.visualViewport?.removeEventListener('scroll', onScrollOrResize);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [showList, updatePanelPosition]);

  if (hasGroup && !editing) {
    const groupColor = groupLabelColor(rowGuest.group, theme);
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          flexWrap: 'nowrap',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: groupColor ?? theme.text,
            lineHeight: 1.35,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={rowGuest.group}
        >
          {rowGuest.group}
        </span>
        <button
          type="button"
          onClick={() => onClear()}
          aria-label="Clear household group"
          title="Clear group"
          style={{
            ...btnBase,
            flexShrink: 0,
            fontSize: 11,
            lineHeight: 1,
            padding: '0 2px',
            minWidth: 18,
            height: 18,
            borderRadius: 4,
            border: 'none',
            background: 'transparent',
            color: theme.mutedLight,
            fontWeight: 600,
            opacity: 0.75,
          }}
        >
          ×
        </button>
      </div>
    );
  }

  if (!hasGroup && !editing) {
    return (
      <div ref={wrapRef} style={{ position: 'relative', width: '100%', minWidth: 88 }}>
        <button
          type="button"
          onClick={() => {
            setEditing(true);
            setOpen(true);
          }}
          aria-label={`Assign household name for guest ${rowGuest.id}`}
          style={{
            ...btnBase,
            fontSize: 10,
            padding: '1px 6px',
            borderRadius: 4,
            border: `1px dashed ${theme.mode === 'dark' ? 'rgba(161, 161, 170, 0.22)' : 'rgba(161, 161, 170, 0.35)'}`,
            background: 'transparent',
            color: theme.mutedLight,
            opacity: 0.72,
            fontWeight: 400,
            lineHeight: 1.35,
          }}
        >
          + household
        </button>
      </div>
    );
  }

  const listPanel = showList && groupPortalMount && (
    <div
      ref={panelRef}
      id={panelId}
      style={{
        position: 'fixed',
        top: panelPos.top,
        left: panelPos.left,
        width: panelPos.width,
        maxHeight: panelPos.maxHeight,
        boxSizing: 'border-box',
        maxWidth: 'min(360px, calc(100vw - 24px))',
        zIndex: groupPopoverZ,
        isolation: 'isolate',
        pointerEvents: 'auto',
        borderRadius: 8,
        border: `1px solid ${theme.border}`,
        backgroundColor: theme.bgPanel,
        boxShadow:
          theme.mode === 'dark'
            ? '0 14px 44px rgba(0, 0, 0, 0.55)'
            : '0 14px 44px rgba(24, 24, 27, 0.16)',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
      }}
    >
      <ul
        id={listId}
        role="listbox"
        aria-label="Guests — household name"
        style={{
          margin: 0,
          padding: 4,
          listStyle: 'none',
          maxHeight: 220,
          overflowY: 'auto',
          backgroundColor: theme.bgPanel,
        }}
      >
        {matches.map((row, i) => (
          <li key={`${rowGuest.id}-grp-${row.id}`} role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={i === hi}
              aria-label={
                row.first && row.last
                  ? `${row.first} ${row.last}`
                  : `${row.first || ''} ${row.last || ''}`.trim() || 'Guest'
              }
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHi(i)}
              onClick={() => commitPick(row)}
              style={{
                ...btnBase,
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '6px 8px',
                border: 'none',
                borderRadius: 6,
                background: i === hi ? theme.indigoTint : 'transparent',
                color: theme.text,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              <span style={{ color: theme.text }}>
                {row.first && row.last ? (
                  <>
                    <span style={{ fontWeight: 650 }}>{row.first}</span>
                    <span style={{ fontWeight: 500, marginLeft: 6 }}>{row.last}</span>
                  </>
                ) : (
                  <span style={{ fontWeight: 500 }}>
                    {`${row.first} ${row.last}`.trim() || 'Guest'}
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', minWidth: 88 }}>
      <div
        role="combobox"
        aria-expanded={showList}
        aria-controls={showList ? panelId : undefined}
        aria-haspopup="listbox"
      >
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={(e) => {
            const next = e.relatedTarget;
            if (
              next &&
              (wrapRef.current?.contains(next) || panelRef.current?.contains(next))
            ) {
              return;
            }
            window.setTimeout(() => {
              const ae = document.activeElement;
              if (!wrapRef.current?.contains(ae) && !panelRef.current?.contains(ae)) {
                setOpen(false);
                if (!draftRef.current.trim()) setEditing(false);
              }
            }, 0);
          }}
          onKeyDown={onKeyDown}
          aria-label={`Pick a guest to name the household for ${rowGuest.id}`}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder="Search first or last name…"
          style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }}
        />
      </div>
      {listPanel ? createPortal(listPanel, groupPortalMount) : null}
    </div>
  );
}

/**
 * Per-guest tag field: suggests palette + in-use tags while typing (or on focus).
 */
function GuestTagSuggestInput({
  guestId,
  guestLabel,
  existingTags,
  theme,
  btnBase,
  onAdd,
  onPersistPreset,
}) {
  const paletteRef = useContext(TagPaletteRefContext);
  const paletteEntries = paletteRef?.current ?? EMPTY_TAG_PALETTE_ENTRIES;
  const tagPortalHost = useContext(TagSuggestPortalHostContext);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [addPresetOpen, setAddPresetOpen] = useState(false);
  const [presetEmoji, setPresetEmoji] = useState('');
  const [presetName, setPresetName] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const presetEmojiRef = useRef(null);
  const draftRef = useRef(draft);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 260 });
  const listId = `tag-suggest-${guestId}`;
  const panelId = `${listId}-panel`;

  /** Compact + until user opens; input only while editing (tags stay on one row with horizontal scroll). */
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    if (!editing) {
      setAddPresetOpen(false);
      setPresetEmoji('');
      setPresetName('');
    }
  }, [editing]);

  useEffect(() => {
    if (addPresetOpen) {
      window.requestAnimationFrame(() => presetEmojiRef.current?.focus());
    }
  }, [addPresetOpen]);

  const existingLower = useMemo(
    () => new Set((existingTags || []).map((t) => String(t).toLowerCase())),
    [existingTags],
  );

  const candidates = useMemo(
    () => paletteEntries.filter((t) => !existingLower.has(t.key)).map((t) => t.label),
    [paletteEntries, existingLower],
  );

  const matches = useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (!q) return candidates.slice(0, 12);
    return candidates.filter((lbl) => lbl.toLowerCase().includes(q)).slice(0, 16);
  }, [candidates, draft]);

  useEffect(() => {
    setHi(0);
  }, [draft]);

  const updatePanelPosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPanelPos(computeSuggestionPanelBox(r, 260, 320));
  }, []);

  useEffect(() => {
    if (!open && !editing && !addPresetOpen) return;
    const close = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
      setAddPresetOpen(false);
      setPresetEmoji('');
      setPresetName('');
      if (editing && !draftRef.current.trim()) setEditing(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, editing, addPresetOpen]);

  const commit = useCallback(
    (raw) => {
      const t = String(raw || '').trim();
      if (!t) return;
      onAdd(t);
      setDraft('');
      setOpen(true);
      setHi(0);
      window.requestAnimationFrame(() => inputRef.current?.focus());
    },
    [onAdd],
  );

  const saveNewPreset = useCallback(() => {
    const full = buildAdminPresetLabel(presetEmoji, presetName);
    if (!full) return;
    onPersistPreset(full);
    onAdd(full);
    setPresetEmoji('');
    setPresetName('');
    setAddPresetOpen(false);
    setDraft('');
    setOpen(true);
    setHi(0);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [presetEmoji, presetName, onPersistPreset, onAdd]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      if (!draft.trim()) {
        setEditing(false);
        if (e.target instanceof HTMLElement) e.target.blur();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      if (matches.length) setHi((h) => Math.min(h + 1, matches.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (matches.length) setHi((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (matches.length && open) {
        commit(matches[Math.min(hi, matches.length - 1)]);
      } else if (draft.trim()) {
        commit(draft);
      }
      return;
    }
  };

  const showDropdown = open && editing;
  const showMatchList = matches.length > 0;
  const tagPortalMount =
    tagPortalHost ?? (typeof document !== 'undefined' ? document.body : null);
  /** Inside dedicated admin layer z-index 1 is enough; body fallback must beat site chrome / #root. */
  const tagPopoverZ = tagPortalHost ? 1 : 2147483000;

  useLayoutEffect(() => {
    if (!showDropdown) return;
    updatePanelPosition();
    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.visualViewport?.addEventListener('resize', onScrollOrResize);
    window.visualViewport?.addEventListener('scroll', onScrollOrResize);
    document.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.visualViewport?.removeEventListener('resize', onScrollOrResize);
      window.visualViewport?.removeEventListener('scroll', onScrollOrResize);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [showDropdown, updatePanelPosition]);

  /** Match tag pill look: same border, fill, type size; circle sized to pill height (~20px with border). */
  const addTagBtnStyle = {
    ...btnBase,
    flexShrink: 0,
    width: 20,
    height: 20,
    minWidth: 20,
    padding: 0,
    borderRadius: '50%',
    boxSizing: 'border-box',
    border: `1px solid ${theme.border}`,
    background: theme.bgMuted,
    color: theme.text,
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
    >
      {!editing ? (
        <button
          type="button"
          onClick={() => {
            setEditing(true);
            setOpen(true);
          }}
          aria-label={`Add tag for ${guestLabel || 'guest'}`}
          title="Add tag"
          style={addTagBtnStyle}
        >
          <span aria-hidden>+</span>
        </button>
      ) : (
        <div
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={showDropdown ? panelId : undefined}
          aria-haspopup="listbox"
          style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'stretch', minWidth: 0 }}
        >
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={(e) => {
              const next = e.relatedTarget;
              if (
                next &&
                (wrapRef.current?.contains(next) || panelRef.current?.contains(next))
              ) {
                return;
              }
              window.setTimeout(() => {
                const ae = document.activeElement;
                if (!wrapRef.current?.contains(ae) && !panelRef.current?.contains(ae)) {
                  setOpen(false);
                  if (!draftRef.current.trim()) setEditing(false);
                }
              }, 0);
            }}
            onKeyDown={onKeyDown}
            aria-label={`Add tag for ${guestLabel || 'guest'}`}
            aria-autocomplete="list"
            autoComplete="off"
            style={{
              fontSize: 12,
              width: 132,
              minWidth: 72,
              maxWidth: 200,
              boxSizing: 'border-box',
              padding: '4px 8px',
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
              background: theme.bgInput,
              color: theme.text,
            }}
          />
        </div>
      )}
      {showDropdown && tagPortalMount
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              style={{
                position: 'fixed',
                top: panelPos.top,
                left: panelPos.left,
                width: panelPos.width,
                maxHeight: panelPos.maxHeight,
                boxSizing: 'border-box',
                maxWidth: 'min(320px, calc(100vw - 24px))',
                zIndex: tagPopoverZ,
                isolation: 'isolate',
                pointerEvents: 'auto',
                borderRadius: 8,
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.bgPanel,
                boxShadow:
                  theme.mode === 'dark'
                    ? '0 14px 44px rgba(0, 0, 0, 0.55)'
                    : '0 14px 44px rgba(24, 24, 27, 0.16)',
                overflowY: 'auto',
                overscrollBehavior: 'contain',
              }}
            >
              {showMatchList ? (
                <ul
                  id={listId}
                  role="listbox"
                  aria-label="Tag suggestions"
                  style={{
                    margin: 0,
                    padding: 4,
                    listStyle: 'none',
                    maxHeight: 200,
                    overflowY: 'auto',
                    backgroundColor: theme.bgPanel,
                  }}
                >
                  {matches.map((label, i) => (
                    <li key={`${guestId}-${label}`} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={i === hi}
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseEnter={() => setHi(i)}
                        onClick={() => commit(label)}
                        style={{
                          ...btnBase,
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '6px 8px',
                          border: 'none',
                          borderRadius: 6,
                          background: i === hi ? theme.indigoTint : 'transparent',
                          color: theme.text,
                          fontSize: 12,
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <div
                style={{
                  borderTop: showMatchList ? `1px solid ${theme.border}` : 'none',
                  padding: 8,
                  backgroundColor: theme.bgMuted,
                }}
              >
                {!addPresetOpen ? (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setAddPresetOpen(true)}
                    aria-label="Add a new default tag with optional emoji"
                    style={{
                      ...btnBase,
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '6px 8px',
                      border: 'none',
                      borderRadius: 6,
                      background: 'transparent',
                      color: theme.accent,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    ＋ Add default tag…
                  </button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        ref={presetEmojiRef}
                        type="text"
                        value={presetEmoji}
                        onChange={(e) => setPresetEmoji(e.target.value.slice(0, 8))}
                        placeholder="Emoji"
                        aria-label="Emoji for new default tag"
                        autoComplete="off"
                        style={{
                          width: 52,
                          flexShrink: 0,
                          textAlign: 'center',
                          fontSize: 16,
                          lineHeight: 1.2,
                          padding: '6px 4px',
                          border: `1px solid ${theme.border}`,
                          borderRadius: 6,
                          background: theme.bgInput,
                          color: theme.text,
                          boxSizing: 'border-box',
                        }}
                      />
                      <input
                        type="text"
                        value={presetName}
                        onChange={(e) => setPresetName(e.target.value.slice(0, 56))}
                        placeholder="Tag name"
                        aria-label="Name for new default tag"
                        autoComplete="off"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            saveNewPreset();
                          }
                        }}
                        style={{
                          flex: 1,
                          minWidth: 120,
                          fontSize: 12,
                          padding: '6px 8px',
                          border: `1px solid ${theme.border}`,
                          borderRadius: 6,
                          background: theme.bgInput,
                          color: theme.text,
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setAddPresetOpen(false);
                          setPresetEmoji('');
                          setPresetName('');
                        }}
                        style={{
                          ...btnBase,
                          padding: '6px 10px',
                          border: `1px solid ${theme.border}`,
                          background: 'transparent',
                          color: theme.muted,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={!presetName.trim()}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={saveNewPreset}
                        style={{
                          ...btnBase,
                          padding: '6px 12px',
                          border: 'none',
                          background: presetName.trim() ? theme.accent : theme.bgMuted,
                          color: presetName.trim() ? theme.onAccent : theme.muted,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: presetName.trim() ? 'pointer' : 'not-allowed',
                        }}
                      >
                        Save & add
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>,
            tagPortalMount,
          )
        : null}
    </div>
  );
}

const AdminGuestSheetRow = memo(
  function AdminGuestSheetRow({
    guest: g,
    isSelected,
    isEntering,
    theme,
    btnBase,
    patchGuest,
    addGuestTag,
    removeGuestTag,
    addCustomTagPreset,
    handleGuestRowCheckboxChange,
    onGroupAssign,
    rsvpAgoNowMs,
  }) {
    const [mobileExpanded, setMobileExpanded] = useState(isEntering);
    const guestLabel = [g.firstName, g.lastName].filter(Boolean).join(' ') || g.email || 'New guest';
    const isBackup = (g.pool ?? GUEST_POOL.INVITED) === GUEST_POOL.BACKUP;
    const pillBase = {
      fontSize: 10,
      fontWeight: 700,
      padding: '3px 8px',
      borderRadius: 999,
      lineHeight: 1.2,
      display: 'inline-block',
      border: '1px solid transparent',
    };
    const whoBub = guestSideBubble(g.guestSide, theme);
    const nameTint = guestSideNameStyle(g.guestSide, theme);
    const { ceremony: hasCeremony, penicuik: hasPenicuik } = siteVisibilityFlags(g.siteVisibility);
    const siteIconOff = siteToggleIconOffColor(theme);
    const rsvpRowMuted = g.adminRsvpStatus === ADMIN_RSVP.DECLINED;
    const rsvpConfirmed = g.adminRsvpStatus === ADMIN_RSVP.CONFIRMED;
    const rsvpStatus = g.adminRsvpStatus ?? ADMIN_RSVP.TENTATIVE;
    const rsvpColor = {
      [ADMIN_RSVP.CONFIRMED]: theme.rsvp.confirmed,
      [ADMIN_RSVP.VERBAL_YES]: theme.rsvp.confirmed,
      [ADMIN_RSVP.TENTATIVE]: theme.rsvp.pending,
      [ADMIN_RSVP.DECLINED]: theme.danger,
    }[rsvpStatus] || theme.muted;
    return (
      <tr
        data-rsvp={rsvpStatus}
        style={{
          '--admin-row-status': rsvpColor,
          '--admin-row-tint': `${rsvpColor}${rsvpConfirmed ? '18' : '0b'}`,
          '--admin-status-tint': `${rsvpColor}1a`,
          '--admin-status-border': `${rsvpColor}55`,
        }}
        className={
          (hasCeremony
            ? 'admin-guest-portal__row--ceremony '
            : 'admin-guest-portal__row--reception-only ') +
          (isBackup ? 'admin-guest-portal__row--backup ' : '') +
          (rsvpRowMuted ? 'admin-guest-portal__row--rsvp-muted ' : '') +
          (rsvpConfirmed ? 'admin-guest-portal__row--rsvp-confirmed ' : '') +
          (isEntering ? 'admin-guest-portal__row--enter ' : '') +
          (mobileExpanded ? 'admin-guest-portal__row--expanded ' : '')
        }
      >
        <td data-label="Select guest"
          style={{ textAlign: 'center', verticalAlign: 'middle' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <label className="admin-guest-portal__selection-hit">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => handleGuestRowCheckboxChange(g.id, e)}
            title="Shift+click another row’s checkbox to select everyone in between"
            aria-label={`Select ${[g.firstName, g.lastName].filter(Boolean).join(' ') || g.email || 'guest'}`}
          />
          </label>
        </td>
        <td data-label=""
          style={{
            width: 7,
            maxWidth: 7,
            padding: 0,
            verticalAlign: 'stretch',
            ...groupStripeStyle(g.group, theme),
          }}
          aria-hidden
        />
        <td data-label="Ceremony" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
          <button
            type="button"
            aria-pressed={hasCeremony}
            title="Ceremony guest"
            aria-label={
              hasCeremony
                ? `Ceremony guest on. Press for reception only.`
                : `Reception only. Press to mark as ceremony guest.`
            }
            onClick={() => {
              const nextCeremony = !hasCeremony;
              patchGuest(g.id, {
                siteVisibility: siteVisibilityFromFlags(nextCeremony, nextCeremony && hasPenicuik),
              });
            }}
            style={{
              ...btnBase,
              margin: 0,
              padding: 2,
              minWidth: 22,
              minHeight: 22,
              border: 'none',
              background: 'transparent',
              borderRadius: 4,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.text,
              lineHeight: 0,
              verticalAlign: 'middle',
            }}
          >
            <SFSymbolStar
              filled={hasCeremony}
              onColor={ceremonyGuestStarColor(theme)}
              offColor={siteIconOff}
            />
          </button>
        </td>
        <td data-label="Staying at Penicuik" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
          <button
            type="button"
            aria-pressed={hasPenicuik}
            title="Staying at Penicuik"
            aria-label={
              hasPenicuik
                ? `Staying at Penicuik on. Press for ceremony without on-site copy.`
                : `Not staying at Penicuik. Press to show on-site copy (turns ceremony on).`
            }
            onClick={() => {
              const nextPen = !hasPenicuik;
              const nextCer = nextPen ? true : hasCeremony;
              patchGuest(g.id, {
                siteVisibility: siteVisibilityFromFlags(nextCer, nextPen),
              });
            }}
            style={{
              ...btnBase,
              margin: 0,
              padding: 2,
              minWidth: 22,
              minHeight: 22,
              border: 'none',
              background: 'transparent',
              borderRadius: 4,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.text,
              lineHeight: 0,
              verticalAlign: 'middle',
            }}
          >
            <SFSymbolHouse filled={hasPenicuik} onColor={theme.indigo} offColor={siteIconOff} />
          </button>
        </td>
        <td data-label="First name" style={{ verticalAlign: 'middle', minWidth: 100 }}>
          <AdminGuestDeferredTextInput
            guestId={g.id}
            value={g.firstName}
            onCommit={(v) => patchGuest(g.id, { firstName: v })}
            aria-label={`First name for ${g.id}`}
            title="Welcome name on the gate"
            placeholder="First name"
            style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', ...nameTint }}
          />
        </td>
        <td data-label="Last name" style={{ verticalAlign: 'middle', minWidth: 100 }}>
          <AdminGuestDeferredTextInput
            guestId={g.id}
            value={g.lastName ?? ''}
            onCommit={(v) => patchGuest(g.id, { lastName: v })}
            aria-label={`Last name for ${g.id}`}
            placeholder="Last name"
            style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', ...nameTint }}
          />
        </td>
        <td data-label="Invite link sent"
          style={{
            textAlign: 'center',
            verticalAlign: 'middle',
            width: 36,
            padding: '4px 2px',
          }}
        >
          <AdminGuestInviteSentCheckbox
            guestId={g.id}
            checked={g.inviteUrlSent === true}
            onCommit={(v) => patchGuest(g.id, { inviteUrlSent: v })}
            title="Mark when you’ve sent this guest the site / Typeform link"
            aria-label={`Invite link sent for ${[g.firstName, g.lastName].filter(Boolean).join(' ') || g.email || 'guest'}`}
          />
        </td>
        <td data-label="RSVP" style={{ verticalAlign: 'middle', minWidth: 128, maxWidth: 220 }}>
          <div className="admin-guest-portal__rsvp-control">
          <select
            value={g.adminRsvpStatus ?? ADMIN_RSVP.TENTATIVE}
            onChange={(e) => patchGuest(g.id, { adminRsvpStatus: e.target.value })}
            aria-label={`RSVP for ${[g.firstName, g.lastName].filter(Boolean).join(' ') || g.email || 'guest'}`}
            title="To invite · Declined · Tentative · Yes (verbal) · Confirmed (form). With several rows ticked, this choice applies to all of them."
            className={`admin-guest-portal__rsvp-pill admin-guest-portal__rsvp-pill--${
              g.adminRsvpStatus ?? ADMIN_RSVP.TENTATIVE
            }`}
            style={{ maxWidth: '100%' }}
          >
            {ADMIN_RSVP_SELECT_ORDER.map((v) => (
              <option key={v} value={v}>
                {ADMIN_RSVP_LABEL[v]}
              </option>
            ))}
          </select>
            <span className="admin-guest-portal__rsvp-compact" aria-hidden="true">
              {ADMIN_RSVP_LABEL[rsvpStatus]}
              <span className="admin-guest-portal__rsvp-chevron" />
            </span>
          </div>
        </td>
        <td data-label="Tags"
          style={{
            verticalAlign: 'middle',
            paddingTop: 4,
            paddingBottom: 4,
            minWidth: 160,
            maxWidth: 360,
          }}
        >
          <div
            className="admin-guest-portal__tag-row"
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'center',
              gap: 4,
              maxWidth: '100%',
              minHeight: 26,
            }}
          >
            <GuestTagSuggestInput
              guestId={g.id}
              guestLabel={g.firstName || g.email || 'guest'}
              existingTags={g.tags}
              theme={theme}
              btnBase={btnBase}
              onAdd={(label) => addGuestTag(g.id, label)}
              onPersistPreset={addCustomTagPreset}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 4,
                flex: '1 1 auto',
                minWidth: 0,
                overflowX: 'auto',
                overflowY: 'visible',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {(g.tags || []).map((tag) => (
                <button
                  key={`${g.id}-${tag}`}
                  type="button"
                  onClick={() => removeGuestTag(g.id, tag)}
                  title="Remove tag"
                  style={{
                    ...pillBase,
                    flexShrink: 0,
                    border: `1px solid ${theme.border}`,
                    background: theme.bgMuted,
                    color: theme.text,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    fontWeight: 600,
                  }}
                >
                  {tag}
                  <span aria-hidden style={{ fontSize: 11, opacity: 0.65 }}>
                    ×
                  </span>
                </button>
              ))}
            </div>
          </div>
        </td>
        <td data-label="Whose guest" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
          <button
            type="button"
            onClick={() =>
              patchGuest(g.id, { guestSide: nextGuestSide(g.guestSide ?? GUEST_SIDE.UNSPECIFIED) })
            }
            title={whoBub.title}
            aria-label={`${whoBub.label}. Click to change whose guest.`}
            style={{
              ...btnBase,
              width: 28,
              height: 28,
              minWidth: 28,
              padding: 0,
              borderRadius: '50%',
              border: whoBub.unset ? `1.5px solid ${whoBub.border}` : `1px solid ${whoBub.border}`,
              background: whoBub.bg,
              color: whoBub.fg,
              fontSize: whoBub.unset ? undefined : whoBub.char === '+' ? 15 : 12,
              fontWeight: 800,
              lineHeight: 1,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: whoBub.unset ? 0.85 : 1,
            }}
          >
            {whoBub.unset ? null : whoBub.char}
          </button>
        </td>
        <td data-label="Email">
          <AdminGuestDeferredTextInput
            guestId={g.id}
            type="email"
            value={g.email}
            onCommit={(v) => patchGuest(g.id, { email: v })}
            aria-label={`Email for ${g.id}`}
            style={{ minWidth: 120 }}
          />
        </td>
        <td data-label="Phone">
          <AdminGuestDeferredTextInput
            guestId={g.id}
            type="tel"
            value={g.phone ?? ''}
            onCommit={(v) => patchGuest(g.id, { phone: v })}
            placeholder="Phone"
            aria-label={`Phone for ${g.id}`}
          />
        </td>
        <td data-label="Household" style={{ verticalAlign: 'middle' }}>
          <GroupAssignSuggestInput
            rowGuest={g}
            theme={theme}
            btnBase={btnBase}
            onAssign={(groupName, anchorGuestId) => onGroupAssign(g.id, groupName, anchorGuestId)}
            onClear={() => patchGuest(g.id, { group: '', isGroupLead: false })}
          />
        </td>
        <td data-label="Accommodation" style={{ verticalAlign: 'middle' }}>
          <AdminGuestDeferredTextInput
            guestId={g.id}
            value={g.accommodation ?? ''}
            onCommit={(v) => patchGuest(g.id, { accommodation: v })}
            aria-label={`Accommodation for ${g.id}`}
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 12 }}
          />
        </td>
        <td data-label="Coach travel" style={{ textAlign: 'center', verticalAlign: 'middle', padding: '4px 2px' }}>
          <div
            role="group"
            aria-label={`Coach travel for ${g.firstName || g.email}`}
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              maxWidth: 118,
              margin: '0 auto',
            }}
          >
            <button
              type="button"
              aria-pressed={g.coachIn === true}
              title="Inbound: 2:30 pickup to Penicuik (yes or not set)"
              aria-label={
                g.coachIn === true
                  ? `Inbound coach yes for ${g.id}. Press to clear.`
                  : `Inbound coach not set for ${g.id}. Press for yes.`
              }
              onClick={() => patchGuest(g.id, { coachIn: g.coachIn === true ? null : true })}
              style={{
                ...btnBase,
                fontSize: 10,
                padding: '2px 5px',
                lineHeight: 1.2,
                minWidth: 36,
                border: `1px solid ${g.coachIn === true ? theme.accent : theme.border}`,
                background: g.coachIn === true ? theme.accentTint : theme.bgInput,
                color: g.coachIn === true ? theme.accent : theme.muted,
                fontWeight: 700,
                borderRadius: 6,
              }}
            >
              {g.coachIn === true ? '2:30' : '—'}
            </button>
            <button
              type="button"
              title="Return bus: tap to cycle 10:30pm → 12:30am → off"
              aria-label={`Return coach ${g.coachOut || 'off'} for ${g.id}. Press to change.`}
              onClick={() => patchGuest(g.id, { coachOut: nextCoachOutValue(g.coachOut) })}
              style={{
                ...btnBase,
                fontSize: 10,
                padding: '2px 5px',
                lineHeight: 1.2,
                minWidth: 40,
                border: `1px solid ${g.coachOut ? theme.indigo : theme.border}`,
                background: g.coachOut ? theme.indigoTint : theme.bgInput,
                color: g.coachOut ? theme.indigo : theme.muted,
                fontWeight: 700,
                borderRadius: 6,
              }}
            >
              {g.coachOut === '10:30' ? '22:30' : g.coachOut === '12:30' ? '00:30' : '—'}
            </button>
          </div>
        </td>
        <td data-label="Train on 29 Dec" style={{ textAlign: 'center', verticalAlign: 'middle', padding: '4px 2px' }}>
          <button
            type="button"
            title="Train on 29 Dec: Yes → No → unset"
            aria-label={`Train 29 Dec for ${g.id}. Press to cycle.`}
            onClick={() => {
              const next =
                g.trainTravelDec29 === true ? false : g.trainTravelDec29 === false ? null : true;
              patchGuest(g.id, { trainTravelDec29: next });
            }}
            style={{
              ...btnBase,
              fontSize: 10,
              padding: '4px 6px',
              lineHeight: 1.2,
              minWidth: 44,
              border: `1px solid ${
                g.trainTravelDec29 === true
                  ? theme.mode === 'dark'
                    ? 'rgba(45, 212, 191, 0.55)'
                    : 'rgba(13, 148, 136, 0.45)'
                  : theme.border
              }`,
              background:
                g.trainTravelDec29 === true
                  ? theme.mode === 'dark'
                    ? 'rgba(45, 212, 191, 0.12)'
                    : 'rgba(13, 148, 136, 0.1)'
                  : g.trainTravelDec29 === false
                    ? theme.mode === 'dark'
                      ? 'rgba(244, 63, 94, 0.1)'
                      : 'rgba(225, 29, 72, 0.08)'
                    : theme.bgInput,
              color:
                g.trainTravelDec29 === true
                  ? theme.mode === 'dark'
                    ? 'rgb(94, 234, 212)'
                    : 'rgb(13, 148, 136)'
                  : g.trainTravelDec29 === false
                    ? theme.mode === 'dark'
                      ? 'rgb(251, 113, 133)'
                      : 'rgb(190, 18, 60)'
                    : theme.muted,
              fontWeight: 700,
              borderRadius: 6,
            }}
          >
            {g.trainTravelDec29 === true ? 'Yes' : g.trainTravelDec29 === false ? 'No' : '—'}
          </button>
        </td>
        <td data-label="Dietary requirements" style={{ verticalAlign: 'middle' }}>
          <AdminGuestDietaryPopoutInput
            value={g.dietary ?? ''}
            onCommit={(v) => patchGuest(g.id, { dietary: v })}
            theme={theme}
            aria-label={`Dietary requirements for ${g.id}`}
          />
        </td>
        <td data-label="Postal address" style={{ verticalAlign: 'middle' }}>
          <AdminGuestDeferredTextInput
            guestId={g.id}
            value={g.address ?? ''}
            onCommit={(v) => patchGuest(g.id, { address: v })}
            aria-label={`Postal address for ${g.id}`}
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 12 }}
          />
        </td>
        <td data-label="RSVP updated"
          style={{
            verticalAlign: 'middle',
            fontSize: 11,
            color: theme.muted,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
          title={
            g.rsvpUpdatedAt && typeof g.rsvpUpdatedAt === 'string' ? g.rsvpUpdatedAt : 'No RSVP update timestamp'
          }
        >
          {formatRsvpUpdatedAgo(g.rsvpUpdatedAt, rsvpAgoNowMs)}
        </td>
        <td className="admin-guest-portal__mobile-summary">
          <button type="button" aria-expanded={mobileExpanded}
            aria-label={`${mobileExpanded ? 'Collapse' : 'Edit'} ${guestLabel}`}
            onClick={() => setMobileExpanded((v) => !v)}>
            <span>
              <strong>{guestLabel}</strong>
              <span className="admin-guest-portal__mobile-details">
                <span className="admin-guest-portal__mobile-meta">
                  {hasCeremony ? 'Ceremony' : 'Reception'}{hasPenicuik ? ' · Penicuik' : ''}{isBackup ? ' · Backup' : ''}
                </span>
                {(g.tags || []).length > 0 ? (
                  <span className="admin-guest-portal__mobile-tags" title={g.tags.join(', ')}>
                    {g.tags.slice(0, 2).map((tag) => <span key={tag} className="admin-guest-portal__mobile-tag">{tag}</span>)}
                    {g.tags.length > 2 ? <span className="admin-guest-portal__mobile-tag-count">+{g.tags.length - 2}</span> : null}
                  </span>
                ) : null}
              </span>
              {g.group ? <span className="admin-guest-portal__mobile-meta admin-guest-portal__mobile-household">{g.group}</span> : null}
            </span>
            <span aria-hidden="true" className="admin-guest-portal__expand-icon">{mobileExpanded ? '−' : '+'}</span>
          </button>
        </td>
      </tr>
    );
  },
  (prev, next) =>
    prev.guest === next.guest &&
    prev.isSelected === next.isSelected &&
    prev.isEntering === next.isEntering &&
    prev.theme === next.theme &&
    prev.btnBase === next.btnBase &&
    prev.rsvpAgoNowMs === next.rsvpAgoNowMs &&
    prev.patchGuest === next.patchGuest &&
    prev.addGuestTag === next.addGuestTag &&
    prev.removeGuestTag === next.removeGuestTag &&
    prev.addCustomTagPreset === next.addCustomTagPreset &&
    prev.handleGuestRowCheckboxChange === next.handleGuestRowCheckboxChange &&
    prev.onGroupAssign === next.onGroupAssign,
);

function AdminGuestPortalContent({ onClose, productionRemoteMode = false }) {
  const { theme, toggleColorMode, colorMode } = useAdminTheme();
  const [guests, setGuests] = useState(getGuestRegistry);
  const allGuestsRegistryRef = useRef(guests);
  allGuestsRegistryRef.current = guests;
  const [searchRaw, setSearchRaw] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const debounceRef = useRef(0);
  const csvInputRef = useRef(null);
  const [adminTab, setAdminTab] = useState('guests');
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 1023px)').matches,
  );
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importBootstrapText, setImportBootstrapText] = useState(null);
  /** Manual list order when `key` is null; otherwise column sort. */
  const [guestListSort, setGuestListSort] = useState({ key: null, asc: true });
  /** Match any selected tag; combine with the other filters and search. */
  const [tagFilterKeys, setTagFilterKeys] = useState(() => new Set());
  /** Sheet filters: ceremony guest / Penicuik stay (combine with AND). */
  const [filterCeremony, setFilterCeremony] = useState(false);
  const [filterPenicuik, setFilterPenicuik] = useState(false);
  /** Train overview chip: guests with train interest Yes (trainTravelDec29 === true). */
  const [filterTrainInterested, setFilterTrainInterested] = useState(false);
  const [filterTrainConfirmed, setFilterTrainConfirmed] = useState(false);
  /** Capacity donut subline: admin RSVP confirmed (form) only. */
  const [filterRsvpConfirmed, setFilterRsvpConfirmed] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  /** Last row used as range anchor for Shift+click (guest id in current filtered list). */
  const selectionAnchorIdRef = useRef(null);
  const headerSelectRef = useRef(null);
  /** Guest ids currently playing the “add row” enter animation (sheet view). */
  const [enteringGuestIds, setEnteringGuestIds] = useState(() => new Set());
  const guestSheetScrollRef = useRef(null);
  const portalRef = useRef(null);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(max-width: 1023px)');
    const syncLayout = () => {
      setIsMobileLayout(media.matches);
      if (!media.matches) return;
      setSelectedIds((prev) => prev.size ? new Set() : prev);
      selectionAnchorIdRef.current = null;
    };
    syncLayout();
    media.addEventListener('change', syncLayout);
    return () => media.removeEventListener('change', syncLayout);
  }, []);
  useLayoutEffect(() => {
    if (guestSheetScrollRef.current) guestSheetScrollRef.current.scrollTop = 0;
  }, [adminTab]);
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const syncViewport = () => {
      portalRef.current?.style.setProperty('--admin-viewport-height', `${viewport.height}px`);
      portalRef.current?.style.setProperty('--admin-viewport-top', `${viewport.offsetTop}px`);
    };
    syncViewport();
    viewport.addEventListener('resize', syncViewport);
    viewport.addEventListener('scroll', syncViewport);
    return () => {
      viewport.removeEventListener('resize', syncViewport);
      viewport.removeEventListener('scroll', syncViewport);
    };
  }, []);
  /** Ignore double Add activation (e.g. overlapping controls) within this window. */
  const lastAddGuestAtRef = useRef(0);
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const [tagSuggestPortalHost, setTagSuggestPortalHost] = useState(null);
  /** Last cloud save failure (Firestore rules, network, doc size, etc.). Cleared on dismiss or successful restore. */
  const [registrySaveError, setRegistrySaveError] = useState(null);
  /** User hid the “browser has more rows” hint for this session. */
  const [backupHintDismissed, setBackupHintDismissed] = useState(false);
  /** sessionStorage-backed: allows a deliberate large reduction to sync to Firestore. */
  const [shrinkGuardBypass, setShrinkGuardBypass] = useState(() =>
    typeof sessionStorage !== 'undefined' &&
    sessionStorage.getItem(GUEST_REGISTRY_SHRINK_GUARD_BYPASS_SESSION_KEY) === '1',
  );
  /** Re-render ~1/min so “RSVP updated” relative times stay fresh. */
  const rsvpAgoNowMs = useTickMs(60000);

  const guestsSnapshotRef = useRef(guests);
  guestsSnapshotRef.current = guests;
  const persistTimerRef = useRef(0);

  /** Save now (clears pending debounced save). Use for imports, add/delete row, tags, migrations. */
  const persistGuestsImmediate = useCallback((nextGuests) => {
    window.clearTimeout(persistTimerRef.current);
    persistTimerRef.current = 0;
    guestsSnapshotRef.current = nextGuests;
    persistRegistry(nextGuests);
  }, []);

  /**
   * Debounced persist for per-keystroke field edits — avoids Firestore + onSnapshot + subscription rerender
   * thrashing on every character (was multi-second “input handler” jank locally).
   */
  const schedulePersistGuests = useCallback((nextGuests) => {
    guestsSnapshotRef.current = nextGuests;
    window.clearTimeout(persistTimerRef.current);
    persistTimerRef.current = window.setTimeout(() => {
      persistTimerRef.current = 0;
      persistRegistry(guestsSnapshotRef.current);
    }, 450);
  }, []);

  useEffect(
    () => () => {
      window.clearTimeout(persistTimerRef.current);
      persistTimerRef.current = 0;
      persistRegistry(guestsSnapshotRef.current);
    },
    [],
  );

  const dismissToast = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback(
    (message, tone = 'info') => {
      const id = ++toastIdRef.current;
      setToasts((list) => [...list, { id, message, tone }]);
      const ms = tone === 'error' ? 5600 : tone === 'success' ? 3800 : 4200;
      window.setTimeout(() => dismissToast(id), ms);
    },
    [dismissToast],
  );

  useEffect(() => {
    const onPersistedOk = () => setShrinkGuardBypass(false);
    window.addEventListener(WEDDING_GUEST_REGISTRY_PERSISTED_OK_EVENT, onPersistedOk);
    return () => window.removeEventListener(WEDDING_GUEST_REGISTRY_PERSISTED_OK_EVENT, onPersistedOk);
  }, []);

  useEffect(() => {
    const onSaveFailed = (e) => {
      const d = e.detail || {};
      const msg =
        typeof d.message === 'string' && d.message.trim()
          ? d.message.trim()
          : 'Guest list did not save to the cloud.';
      setRegistrySaveError(msg);
      pushToast(msg, 'error');
    };
    window.addEventListener(WEDDING_GUEST_REGISTRY_SAVE_FAILED_EVENT, onSaveFailed);
    return () => window.removeEventListener(WEDDING_GUEST_REGISTRY_SAVE_FAILED_EVENT, onSaveFailed);
  }, [pushToast]);

  const localBackupGuests = readGuestRegistryLocalStorageDraft();
  const showBrowserBackupHint =
    isRemoteGuestRegistryConfigured() &&
    !backupHintDismissed &&
    localBackupGuests.length > 0 &&
    localBackupGuests.length >= guests.length + 5;

  useEffect(() => {
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => setSearchDebounced(searchRaw.trim().toLowerCase()), 200);
    return () => window.clearTimeout(debounceRef.current);
  }, [searchRaw]);

  const syncGuestsFromRegistry = useCallback(() => {
    let list = dedupeGuestsById(getGuestRegistry());
    if (
      list.length === 0 &&
      isRemoteGuestRegistryConfigured() &&
      !isRemoteGuestRegistryHydrated()
    ) {
      const draft = readGuestRegistryLocalStorageDraft();
      if (draft.length > 0) {
        list = dedupeGuestsById(draft);
      }
    }
    queueMicrotask(() => setGuests(list));
  }, []);

  useEffect(() => {
    const unsub = subscribeGuestRegistry(syncGuestsFromRegistry);
    const onVis = () => {
      if (document.visibilityState === 'visible') syncGuestsFromRegistry();
    };
    const onFocus = () => syncGuestsFromRegistry();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      unsub();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, [syncGuestsFromRegistry]);

  const [customTagPresets, setCustomTagPresets] = useState(readAdminCustomTagPresets);

  /** Tags from guests + default palette + admin custom palette (presets first, then other guest tags). */
  const tagPaletteOrdered = useMemo(() => {
    const fromGuestsMap = new Map();
    for (const guest of guests) {
      for (const t of guest.tags || []) {
        const d = String(t).trim();
        if (!d) continue;
        const key = d.toLowerCase();
        if (!fromGuestsMap.has(key)) fromGuestsMap.set(key, { key, label: d, count: 0 });
        fromGuestsMap.get(key).count += 1;
      }
    }

    const presetOrder = [...DEFAULT_TAG_PRESETS, ...customTagPresets];
    const mergedKeys = new Set();
    const out = [];
    for (const presetLabel of presetOrder) {
      const label = String(presetLabel).trim();
      if (!label) continue;
      const key = label.toLowerCase();
      if (mergedKeys.has(key)) continue;
      mergedKeys.add(key);
      const g = fromGuestsMap.get(key);
      out.push({
        key,
        label: g?.label ?? label,
        count: g?.count ?? 0,
      });
    }

    const rest = [...fromGuestsMap.values()]
      .filter((t) => !mergedKeys.has(t.key))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...out, ...rest];
  }, [guests, customTagPresets]);

  const tagPaletteRegistryRef = useRef(tagPaletteOrdered);
  tagPaletteRegistryRef.current = tagPaletteOrdered;

  /** Default + custom presets only (for “add to selected” shortcuts). */
  const presetPaletteForQuickAdd = useMemo(() => {
    const keys = new Set([
      ...DEFAULT_TAG_PRESETS.map((l) => String(l).trim().toLowerCase()).filter(Boolean),
      ...customTagPresets.map((l) => String(l).trim().toLowerCase()).filter(Boolean),
    ]);
    return tagPaletteOrdered.filter((t) => keys.has(t.key));
  }, [tagPaletteOrdered, customTagPresets]);

  const siteFilterCounts = useMemo(() => {
    let ceremony = 0;
    let penicuik = 0;
    for (const g of guests) {
      const v = siteVisibilityFlags(g.siteVisibility);
      if (v.ceremony) ceremony += 1;
      if (v.penicuik) penicuik += 1;
    }
    return { ceremony, penicuik };
  }, [guests]);

  const filteredSorted = useMemo(() => {
    let list = guests;
    if (tagFilterKeys.size > 0) {
      list = list.filter((g) =>
        (g.tags || []).some((t) => tagFilterKeys.has(String(t).trim().toLowerCase())),
      );
    }
    if (filterCeremony) {
      list = list.filter((g) => siteVisibilityFlags(g.siteVisibility).ceremony);
    }
    if (filterPenicuik) {
      list = list.filter((g) => siteVisibilityFlags(g.siteVisibility).penicuik);
    }
    if (filterTrainInterested) {
      list = list.filter((g) => g.trainTravelDec29 === true);
    }
    if (filterTrainConfirmed) {
      list = list.filter(hasConfirmedTrainBooking);
    }
    if (filterRsvpConfirmed) {
      list = list.filter((g) => g.adminRsvpStatus === ADMIN_RSVP.CONFIRMED);
    }
    if (searchDebounced) {
      list = list.filter((g) => {
        const tagHay = (g.tags || []).join(' ');
        const hay = `${g.email} ${g.firstName} ${g.lastName || ''} ${g.id} ${g.notes || ''} ${g.address || ''} ${g.dietary || ''} ${g.accommodation || ''} ${g.group || ''} ${g.phone || ''} ${g.coachIn === true ? 'coach in' : ''} ${g.coachOut || ''} ${g.trainTravelDec29 === true ? 'train 29 dec' : ''} ${tagHay}`.toLowerCase();
        return hay.includes(searchDebounced);
      });
    }
    const base = sortGuestsForDisplay(list);
    if (!guestListSort.key) return base;
    return [...base].sort((a, b) => compareGuestRowsForSort(a, b, guestListSort.key, guestListSort.asc));
  }, [
    guests,
    searchDebounced,
    tagFilterKeys,
    filterCeremony,
    filterPenicuik,
    filterTrainInterested,
    filterTrainConfirmed,
    filterRsvpConfirmed,
    guestListSort,
  ]);

  const trainConfirmedGuestCount = useMemo(
    () => guests.filter(hasConfirmedTrainBooking).length,
    [guests],
  );

  const dashboardInvitedGuests = useMemo(
    () => guests.filter((g) => (g.pool ?? GUEST_POOL.INVITED) === GUEST_POOL.INVITED),
    [guests],
  );
  const dashboardCeremonyInvitedGuests = useMemo(
    () => dashboardInvitedGuests.filter((g) => siteVisibilityFlags(g.siteVisibility).ceremony),
    [dashboardInvitedGuests],
  );

  const activeFilterCount = [filterCeremony, filterPenicuik, filterTrainInterested, filterTrainConfirmed, filterRsvpConfirmed].filter(Boolean).length + tagFilterKeys.size;
  const selectedCount = selectedIds.size;

  const allInViewSelected =
    filteredSorted.length > 0 && filteredSorted.every((g) => selectedIds.has(g.id));
  const someInViewSelected = filteredSorted.some((g) => selectedIds.has(g.id));

  useEffect(() => {
    const el = headerSelectRef.current;
    if (el) el.indeterminate = someInViewSelected && !allInViewSelected;
  }, [someInViewSelected, allInViewSelected, adminTab]);

  const toggleRowSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAllInView = useCallback(() => {
    setSelectedIds(new Set(filteredSorted.map((g) => g.id)));
    selectionAnchorIdRef.current = filteredSorted[0]?.id ?? null;
  }, [filteredSorted]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    selectionAnchorIdRef.current = null;
  }, []);

  /** Shift+click selects a contiguous range in the current filtered/sorted list (like spreadsheet rows). */
  const handleGuestRowCheckboxChange = useCallback(
    (guestId, e) => {
      if (e.shiftKey) {
        const anchorId = selectionAnchorIdRef.current;
        if (anchorId != null) {
          const ia = filteredSorted.findIndex((x) => x.id === anchorId);
          const ib = filteredSorted.findIndex((x) => x.id === guestId);
          if (ia !== -1 && ib !== -1) {
            const lo = Math.min(ia, ib);
            const hi = Math.max(ia, ib);
            setSelectedIds(new Set(filteredSorted.slice(lo, hi + 1).map((row) => row.id)));
            return;
          }
        }
      }
      toggleRowSelected(guestId);
      selectionAnchorIdRef.current = guestId;
    },
    [filteredSorted, toggleRowSelected],
  );

  const resetListFilters = useCallback((notify = true) => {
    setSearchRaw('');
    setTagFilterKeys(new Set());
    setFilterCeremony(false);
    setFilterPenicuik(false);
    setFilterTrainInterested(false);
    setFilterTrainConfirmed(false);
    setFilterRsvpConfirmed(false);
    setGuestListSort({ key: null, asc: true });
    if (notify) pushToast('Filters reset', 'info');
  }, [pushToast]);

  const toggleTagFilter = useCallback((key) => {
    setTagFilterKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleTrainConfirmedFilter = useCallback(() => {
    setFilterTrainConfirmed((v) => !v);
  }, []);

  const toggleTrainInterestedFilter = useCallback(() => {
    setFilterTrainInterested((v) => !v);
  }, []);

  const toggleCeremonyFilter = useCallback(() => {
    setFilterCeremony((v) => !v);
  }, []);

  const toggleRsvpConfirmedFilter = useCallback(() => {
    setFilterRsvpConfirmed((v) => !v);
  }, []);

  const toggleGuestListSort = useCallback((/** @type {GuestListSortKey} */ key) => {
    setGuestListSort((prev) => {
      if (prev.key !== key) return { key, asc: true };
      return { key, asc: !prev.asc };
    });
  }, []);

  const addGuestTag = useCallback(
    (id, raw) => {
      const t = String(raw || '').trim();
      if (!t) return;
      const idSet =
        selectedIds.has(id) && selectedIds.size > 0 ? new Set(selectedIds) : new Set([id]);
      setGuests((prev) => {
        const next = prev.map((row) => {
          if (!idSet.has(row.id)) return row;
          const nextTags = normalizeTags([...(row.tags || []), t]);
          return { ...row, tags: nextTags };
        });
        persistGuestsImmediate(next);
        return next;
      });
      if (idSet.size > 1) {
        window.setTimeout(() => pushToast(`Added tag to ${idSet.size} guests`, 'success'), 0);
      }
    },
    [selectedIds, pushToast, persistGuestsImmediate],
  );

  const addTagToSelectedGuests = useCallback(
    (tagLabel) => {
      const t = String(tagLabel || '').trim();
      if (!t || selectedIds.size === 0) return;
      const idSet = selectedIds;
      const n = idSet.size;
      setGuests((prev) => {
        const next = prev.map((row) => {
          if (!idSet.has(row.id)) return row;
          const nextTags = normalizeTags([...(row.tags || []), t]);
          return { ...row, tags: nextTags };
        });
        persistGuestsImmediate(next);
        return next;
      });
      window.setTimeout(() => pushToast(`Added tag to ${n} guest${n === 1 ? '' : 's'}`, 'success'), 0);
    },
    [selectedIds, pushToast, persistGuestsImmediate],
  );

  const addCustomTagPreset = useCallback(
    (raw) => {
      const t = String(raw || '').trim().slice(0, 64);
      if (!t) return;
      const k = t.toLowerCase();
      setCustomTagPresets((prev) => {
        const keys = new Set([
          ...DEFAULT_TAG_PRESETS.map((x) => String(x).trim().toLowerCase()).filter(Boolean),
          ...prev.map((x) => String(x).trim().toLowerCase()).filter(Boolean),
        ]);
        if (keys.has(k)) {
          window.setTimeout(() => pushToast('That tag is already in the palette', 'info'), 0);
          return prev;
        }
        const next = [...prev, t];
        writeAdminCustomTagPresets(next);
        window.setTimeout(() => pushToast('Saved to palette', 'success'), 0);
        return next;
      });
    },
    [pushToast],
  );

  const removeGuestTag = useCallback(
    (id, tagLabel) => {
      const key = String(tagLabel).toLowerCase();
      const idSet =
        selectedIds.has(id) && selectedIds.size > 0 ? new Set(selectedIds) : new Set([id]);
      setGuests((prev) => {
        const next = prev.map((row) => {
          if (!idSet.has(row.id)) return row;
          const tags = (row.tags || []).filter((x) => String(x).toLowerCase() !== key);
          return { ...row, tags };
        });
        persistGuestsImmediate(next);
        return next;
      });
      if (idSet.size > 1) {
        window.setTimeout(() => pushToast(`Removed tag from ${idSet.size} guests`, 'success'), 0);
      }
    },
    [selectedIds, pushToast, persistGuestsImmediate],
  );

  const removeSelectedGuests = useCallback(() => {
    if (!selectedIds.size) return;
    const ok = window.confirm(
      `Remove ${selectedIds.size} guest(s) from the registry? This cannot be undone.`,
    );
    if (!ok) return;
    const idSet = selectedIds;
    const n = idSet.size;
    setGuests((prev) => {
      const next = prev.filter((g) => !idSet.has(g.id));
      persistGuestsImmediate(next);
      return next;
    });
    window.setTimeout(() => pushToast(`Removed ${n} guest${n === 1 ? '' : 's'}`, 'success'), 0);
    clearSelection();
  }, [selectedIds, pushToast, clearSelection, persistGuestsImmediate]);

  const onImportMerged = useCallback(
    (merged, { msg, warnings }) => {
      setGuests(merged);
      persistGuestsImmediate(merged);
      const parts = [msg];
      if (warnings?.length) parts.push(warnings.slice(0, 3).join(' '));
      window.setTimeout(
        () => pushToast(parts.join(' '), warnings?.length ? 'info' : 'success'),
        0,
      );
    },
    [pushToast, persistGuestsImmediate],
  );

  const patchGuest = useCallback(
    (id, updates) => {
      const keys = Object.keys(updates);
      const dropdownBulkOnly =
        keys.length > 0 && keys.every((k) => ADMIN_BULK_DROPDOWN_KEYS.has(k));
      const applyToMany =
        dropdownBulkOnly && selectedIds.size > 1 && selectedIds.has(id);
      const targets = applyToMany ? selectedIds : new Set([id]);
      setGuests((prev) => {
        const next = prev.map((g) => (targets.has(g.id) ? mergeGuestPatch(g, updates) : g));
        schedulePersistGuests(next);
        return next;
      });
    },
    [schedulePersistGuests, selectedIds],
  );

  const handleGroupAssign = useCallback(
    (guestId, groupName, anchorGuestId) => {
      setGuests((prev) => {
        const row = prev.find((r) => r.id === guestId);
        if (!row) return prev;
        const prevKey = (row.group || '').trim();
        const nextKey = String(groupName || '').trim();
        const selfIsAnchor = anchorGuestId === guestId;
        const next = prev.map((r) => {
          if (r.id === guestId) {
            const updates =
              prevKey !== nextKey ? { group: groupName, isGroupLead: false } : { group: groupName };
            return mergeGuestPatch(r, updates);
          }
          if (!selfIsAnchor && anchorGuestId && r.id === anchorGuestId) {
            return mergeGuestPatch(r, { group: groupName });
          }
          return r;
        });
        schedulePersistGuests(next);
        return next;
      });
    },
    [schedulePersistGuests],
  );

  const addGuest = useCallback(() => {
    const now = Date.now();
    if (now - lastAddGuestAtRef.current < 450) return;
    lastAddGuestAtRef.current = now;
    const g = defaultGuest();
    resetListFilters(false);
    setMobileToolsOpen(false);
    setGuests((prev) => {
      const next = dedupeGuestsById([g, ...prev]);
      persistGuestsImmediate(next);
      return next;
    });
    setEnteringGuestIds((prev) => {
      const n = new Set(prev);
      n.add(g.id);
      return n;
    });
    window.setTimeout(() => {
      setEnteringGuestIds((prev) => {
        const n = new Set(prev);
        n.delete(g.id);
        return n;
      });
    }, 420);
    window.setTimeout(() => {
      guestSheetScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
    window.setTimeout(() => pushToast('Guest row added', 'success'), 0);
  }, [pushToast, persistGuestsImmediate, resetListFilters]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const btnBase = useMemo(
    () => ({
      font: 'inherit',
      fontWeight: 600,
      cursor: 'pointer',
      borderRadius: 6,
    }),
    [],
  );

  const downloadRegistryBackup = useCallback(() => {
    downloadGuestRegistryBackup(guests, customTagPresets);
    pushToast(
      `Backup downloaded (${guests.length} guests, ${customTagPresets.length} custom tag presets)`,
      'success',
    );
  }, [guests, customTagPresets, pushToast]);

  const overviewStrip = (
    <AdminRsvpOverviewStrip
      compact={isMobileLayout}
      invitedGuests={dashboardInvitedGuests}
      trainConfirmedGuestCount={trainConfirmedGuestCount}
      ceremonyGuestCount={dashboardCeremonyInvitedGuests.length}
      theme={theme}
      filterCeremony={filterCeremony}
      onToggleCeremonyFilter={toggleCeremonyFilter}
      filterTrainInterested={filterTrainInterested}
      onToggleTrainInterestedFilter={toggleTrainInterestedFilter}
      filterTrainConfirmed={filterTrainConfirmed}
      onToggleTrainConfirmedFilter={toggleTrainConfirmedFilter}
      filterRsvpConfirmed={filterRsvpConfirmed}
      onToggleRsvpConfirmedFilter={toggleRsvpConfirmedFilter}
    />
  );

  return (
    <TagSuggestPortalHostContext.Provider value={tagSuggestPortalHost}>
    <AdminAllGuestsRefContext.Provider value={allGuestsRegistryRef}>
    <TagPaletteRefContext.Provider value={tagPaletteRegistryRef}>
    <div
      className={`admin-guest-portal${mobileToolsOpen ? ' admin-guest-portal--tools-open' : ''}${mobileMenuOpen ? ' admin-guest-portal--menu-open' : ''}`}
      ref={portalRef}

      role="dialog"
      aria-modal="true"
      aria-labelledby={adminTab === 'voicenotes' ? 'admin-voice-notes-title' : 'admin-guest-portal-title'}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10002,
        background: theme.bgMuted,
        color: theme.text,
        colorScheme: colorMode === 'dark' ? 'dark' : 'light',
        '--admin-bg': theme.bgPanel,
        '--admin-input': theme.bgInput,
        '--admin-border': theme.border,
        '--admin-text': theme.text,
        '--admin-muted': theme.muted,
        '--admin-accent': theme.accent,
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        overflow: 'hidden',
        overscrollBehavior: 'contain',
      }}
    >
      <style>{`
        @keyframes admin-guest-portal-toast-in {
          from {
            opacity: 0;
            transform: translateX(14px) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        .admin-guest-portal__toast {
          animation: admin-guest-portal-toast-in 0.42s cubic-bezier(0.22, 1, 0.32, 1) both;
          pointer-events: auto;
          cursor: pointer;
          text-align: left;
          max-width: min(360px, calc(100vw - 32px));
          padding: 10px 14px 10px 12px;
          border-radius: 10px;
          border: 1px solid ${theme.border};
          font-size: 13px;
          font-weight: 500;
          line-height: 1.4;
          box-shadow: 0 10px 36px ${theme.modalOverlay};
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          background: ${theme.bgPanel};
          -webkit-appearance: none;
          appearance: none;
        }
        .admin-guest-portal__toast:hover {
          transform: translateY(-1px);
        }
        .admin-guest-portal__toast--success {
          border-left: 4px solid ${theme.mode === 'dark' ? '#4ade80' : '#16a34a'};
          background: ${theme.mode === 'dark' ? '#14261c' : '#ecfdf5'};
        }
        .admin-guest-portal__toast--error {
          border-left: 4px solid ${theme.danger};
          background: ${theme.mode === 'dark' ? '#2a1517' : '#fef2f2'};
        }
        .admin-guest-portal__toast--info {
          border-left: 4px solid ${theme.accent};
          background: ${theme.mode === 'dark' ? '#151a22' : '#f1f5f9'};
        }
        .admin-guest-portal__tag-row {
          scrollbar-width: thin;
          scrollbar-color: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent;
        }
        .admin-guest-portal__tag-row::-webkit-scrollbar {
          height: 5px;
        }
        .admin-guest-portal__tag-row::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)'};
        }
        .admin-guest-portal input[type="text"],
        .admin-guest-portal input[type="email"],
        .admin-guest-portal input[type="search"],
        .admin-guest-portal input[type="url"],
        .admin-guest-portal input[type="number"],
        .admin-guest-portal select {
          font: inherit;
          font-size: 13px;
          padding: 6px 8px;
          border: 1px solid ${theme.border};
          border-radius: 6px;
          background: ${theme.bgInput};
          color: ${theme.text};
          width: 100%;
          box-sizing: border-box;
        }
        .admin-guest-portal select { cursor: pointer; }
        .admin-guest-portal select option { background: ${theme.bgPanel}; color: ${theme.text}; }
        .admin-guest-portal table { border-collapse: collapse; width: 100%; }
        .admin-guest-portal th {
          text-align: left;
          font-size: 11px;
          font-weight: 600;
          color: ${theme.muted};
          text-transform: uppercase;
          letter-spacing: 0.04em;
          padding: 8px 10px;
          border-bottom: 1px solid ${theme.border};
          background: ${theme.bgPanel};
          position: sticky;
          top: 0;
          z-index: 1;
        }
        .admin-guest-portal td {
          padding: 8px 10px;
          border-bottom: 1px solid ${theme.border};
          vertical-align: middle;
          font-size: 13px;
        }
        .admin-guest-portal__detail-row td {
          background: ${theme.bgMuted};
          border-bottom: 1px solid ${theme.border};
          padding: 12px 14px 14px;
          vertical-align: top;
        }
        .admin-guest-portal__body {
          flex: 1;
          min-height: 0;
          width: 100%;
          overflow-x: hidden;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
          box-sizing: border-box;
          background: ${theme.bg};
        }
        .admin-guest-portal__body-inner {
          box-sizing: border-box;
          min-width: 0;
        }
        .admin-guest-portal__tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          align-items: flex-end;
        }
        .admin-guest-portal__tab {
          padding: 8px 16px;
          margin-bottom: -1px;
          border: 1px solid transparent;
          border-bottom: none;
          border-radius: 8px 8px 0 0;
          background: transparent;
          color: ${theme.muted};
          font: inherit;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
        }
        .admin-guest-portal__tab:hover {
          color: ${theme.text};
        }
        .admin-guest-portal__tab--active {
          color: ${theme.text};
          background: ${theme.tabActiveBg};
          border-color: ${theme.border};
          border-bottom-color: ${theme.tabActiveBg};
        }
        .admin-guest-portal--sheet th,
        .admin-guest-portal--sheet td {
          padding: 3px 4px;
          font-size: 12px;
        }
        .admin-guest-portal--sheet tbody td {
          position: relative;
        }
        .admin-guest-portal--sheet tbody td::after {
          content: '';
          position: absolute;
          inset: 0;
          background: ${theme.rowHover};
          opacity: 0;
          pointer-events: none;
          z-index: 0;
          transition: opacity 0.14s ease;
        }
        @media (hover: hover) and (pointer: fine) {
          .admin-guest-portal--sheet tbody tr:hover > td::after {
            opacity: 1;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .admin-guest-portal--sheet tbody td::after {
            transition: none;
          }
        }
        .admin-guest-portal--sheet tbody td > * {
          position: relative;
          z-index: 1;
        }
        /* Sheet: never vertically sticky thead (avoids header over guest toolbar). Global .admin-guest-portal th uses sticky — override hard. */
        .admin-guest-portal--sheet thead th {
          position: static !important;
          top: auto !important;
          z-index: auto !important;
          box-shadow: none !important;
        }
        @keyframes admin-guest-portal-row-enter {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .admin-guest-portal--sheet tbody tr.admin-guest-portal__row--enter {
          animation: admin-guest-portal-row-enter 0.38s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .admin-guest-portal--sheet tbody tr.admin-guest-portal__row--enter {
            animation: none;
            opacity: 1;
            transform: none;
          }
        }
        /* First-name column: sticky. Opaque fills match row tints so rgba row BGs don’t stack darker over scrolling cells. */
        .admin-guest-portal--sheet tbody td:nth-child(5) {
          position: sticky;
          left: 0;
          z-index: 2;
          box-sizing: border-box;
          box-shadow: none;
        }
        .admin-guest-portal--sheet
          tbody
          tr.admin-guest-portal__row--ceremony:not(.admin-guest-portal__row--backup):not(
            .admin-guest-portal__row--rsvp-confirmed
          )
          > td:nth-child(5) {
          background: ${rgbCss(ceremonyTintRgb(theme))};
        }
        .admin-guest-portal--sheet
          tbody
          tr.admin-guest-portal__row--reception-only:not(.admin-guest-portal__row--backup):not(
            .admin-guest-portal__row--rsvp-confirmed
          )
          > td:nth-child(5) {
          background: ${rgbCss(receptionTintRgb(theme))};
        }
        .admin-guest-portal--sheet
          tbody
          tr.admin-guest-portal__row--backup:not(.admin-guest-portal__row--rsvp-confirmed)
          > td:nth-child(5) {
          background: ${rgbCss(backupTintRgb(theme))};
        }
        .admin-guest-portal--sheet
          tbody
          tr.admin-guest-portal__row--rsvp-confirmed.admin-guest-portal__row--ceremony:not(
            .admin-guest-portal__row--backup
          )
          > td:nth-child(5) {
          background: ${rgbCss(confirmedGreenTintRgb(theme, ceremonyTintRgb(theme)))};
        }
        .admin-guest-portal--sheet
          tbody
          tr.admin-guest-portal__row--rsvp-confirmed.admin-guest-portal__row--reception-only:not(
            .admin-guest-portal__row--backup
          )
          > td:nth-child(5) {
          background: ${rgbCss(confirmedGreenTintRgb(theme, receptionTintRgb(theme)))};
        }
        .admin-guest-portal--sheet
          tbody
          tr.admin-guest-portal__row--rsvp-confirmed.admin-guest-portal__row--backup
          > td:nth-child(5) {
          background: ${rgbCss(confirmedGreenTintRgb(theme, backupTintRgb(theme)))};
        }
        .admin-guest-portal--sheet input[type="text"],
        .admin-guest-portal--sheet input[type="email"],
        .admin-guest-portal--sheet input[type="tel"],
        .admin-guest-portal--sheet input[type="number"] {
          font-size: 12px !important;
          line-height: 1.35 !important;
          padding: 1px 2px !important;
          margin: 0 !important;
          border: none !important;
          border-radius: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
          color: ${theme.text} !important;
        }
        .admin-guest-portal--sheet input[type="number"]::-webkit-outer-spin-button,
        .admin-guest-portal--sheet input[type="number"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .admin-guest-portal--sheet input[type="number"] {
          -moz-appearance: textfield;
        }
        .admin-guest-portal--sheet input[type="text"]:focus,
        .admin-guest-portal--sheet input[type="email"]:focus,
        .admin-guest-portal--sheet input[type="tel"]:focus,
        .admin-guest-portal--sheet input[type="number"]:focus {
          outline: none !important;
          box-shadow: 0 0 0 1px ${theme.accent} !important;
          border-radius: 2px !important;
        }
        .admin-guest-portal--sheet input.admin-guest-portal__dietary-popout-anchor[data-dietary-expanded='true'] {
          outline: none !important;
          box-shadow: 0 0 0 1px ${theme.accent} !important;
          border-radius: 2px !important;
        }
        .admin-guest-portal--sheet select {
          font-size: 12px !important;
          line-height: 1.35 !important;
          padding: 1px 4px !important;
          margin: 0 !important;
          border: none !important;
          border-radius: 0 !important;
          background-color: transparent !important;
          box-shadow: none !important;
          color: ${theme.text} !important;
          cursor: pointer;
        }
        .admin-guest-portal--sheet select:focus {
          outline: none !important;
          box-shadow: 0 0 0 1px ${theme.accent} !important;
          border-radius: 2px !important;
        }
        .admin-guest-portal--sheet select.admin-guest-portal__rsvp-pill:focus {
          border-radius: 999px !important;
        }
        .admin-guest-portal--sheet select.admin-guest-portal__rsvp-pill {
          border-radius: 999px !important;
          padding: 4px 20px 4px 9px !important;
          border: 1px solid transparent !important;
          font-weight: 600 !important;
          font-size: 10px !important;
          letter-spacing: 0.03em;
          text-transform: none;
          width: auto !important;
          max-width: 100%;
          line-height: 1.35 !important;
        }
        .admin-guest-portal--sheet select.admin-guest-portal__rsvp-pill--not_invited {
          background: ${theme.mode === 'dark' ? 'rgba(156, 163, 175, 0.2)' : 'rgba(113, 113, 122, 0.12)'} !important;
          border-color: ${theme.mode === 'dark' ? 'rgba(156, 163, 175, 0.4)' : 'rgba(113, 113, 122, 0.28)'} !important;
          color: ${theme.muted} !important;
        }
        .admin-guest-portal--sheet select.admin-guest-portal__rsvp-pill--declined {
          background: ${theme.mode === 'dark' ? 'rgba(248, 113, 113, 0.16)' : 'rgba(220, 38, 38, 0.1)'} !important;
          border-color: ${theme.mode === 'dark' ? 'rgba(248, 113, 113, 0.45)' : 'rgba(220, 38, 38, 0.32)'} !important;
          color: ${theme.danger} !important;
        }
        .admin-guest-portal--sheet select.admin-guest-portal__rsvp-pill--tentative {
          background: ${theme.mode === 'dark' ? 'rgba(251, 191, 36, 0.14)' : 'rgba(217, 119, 6, 0.12)'} !important;
          border-color: ${theme.mode === 'dark' ? 'rgba(251, 191, 36, 0.42)' : 'rgba(217, 119, 6, 0.35)'} !important;
          color: ${theme.rsvp.pending} !important;
        }
        .admin-guest-portal--sheet select.admin-guest-portal__rsvp-pill--verbal_yes {
          background: transparent !important;
          border-color: ${theme.mode === 'dark' ? 'rgba(74, 222, 128, 0.45)' : 'rgba(22, 163, 74, 0.35)'} !important;
          color: ${theme.rsvp.confirmed} !important;
        }
        .admin-guest-portal--sheet select.admin-guest-portal__rsvp-pill--confirmed {
          background: ${theme.mode === 'dark' ? 'rgba(74, 222, 128, 0.14)' : 'rgba(22, 163, 74, 0.12)'} !important;
          border-color: ${theme.mode === 'dark' ? 'rgba(74, 222, 128, 0.45)' : 'rgba(22, 163, 74, 0.35)'} !important;
          color: ${theme.rsvp.confirmed} !important;
        }
        /* Declined: whole row de-emphasised; RSVP column skips row opacity/grayscale so pill opacity isn’t doubled. */
        .admin-guest-portal--sheet tr.admin-guest-portal__row--rsvp-muted > td {
          opacity: 0.5;
          filter: grayscale(0.35);
        }
        .admin-guest-portal--sheet tr.admin-guest-portal__row--rsvp-muted > td:nth-child(9) {
          opacity: 1;
          filter: none;
        }
        .admin-guest-portal--sheet input.admin-guest-portal__invite-link-sent {
          -webkit-appearance: none;
          appearance: none;
          width: 17px;
          height: 17px;
          min-width: 17px;
          min-height: 17px;
          border-radius: 50% !important;
          border: 1.5px solid ${theme.border} !important;
          background: ${theme.bgInput} !important;
          cursor: pointer;
          margin: 0;
          padding: 0 !important;
          vertical-align: middle;
          box-shadow: none !important;
          flex-shrink: 0;
        }
        .admin-guest-portal--sheet input.admin-guest-portal__invite-link-sent:focus-visible {
          outline: 2px solid ${theme.accent};
          outline-offset: 1px;
        }
        .admin-guest-portal--sheet input.admin-guest-portal__invite-link-sent:checked {
          border-color: ${theme.mode === 'dark' ? 'rgb(52, 211, 153)' : 'rgb(22, 163, 74)'} !important;
          background: ${theme.mode === 'dark' ? 'rgb(52, 211, 153)' : 'rgb(22, 163, 74)'} !important;
          box-shadow: inset 0 0 0 3px ${theme.bgPanel} !important;
        }
        .admin-guest-portal--sheet tr.admin-guest-portal__row--rsvp-muted
          select.admin-guest-portal__rsvp-pill--declined {
          opacity: 0.6;
        }
        .admin-guest-portal__row--ceremony td {
          background: ${ceremonyGuestRowTint(theme)};
        }
        .admin-guest-portal__row--reception-only td {
          background: ${theme.mode === 'dark' ? 'rgba(161, 161, 170, 0.05)' : 'rgba(24, 24, 27, 0.02)'};
        }
        .admin-guest-portal__row--backup td {
          background: ${theme.backupRowBg};
        }
        .admin-guest-portal__row--rsvp-confirmed.admin-guest-portal__row--ceremony td {
          background: ${confirmedGuestRowBackground(theme, ceremonyGuestRowTint(theme))};
        }
        .admin-guest-portal__row--rsvp-confirmed.admin-guest-portal__row--reception-only td {
          background: ${confirmedGuestRowBackground(
            theme,
            theme.mode === 'dark' ? 'rgba(161, 161, 170, 0.05)' : 'rgba(24, 24, 27, 0.02)',
          )};
        }
        .admin-guest-portal__row--rsvp-confirmed.admin-guest-portal__row--backup td {
          background: ${confirmedGuestRowBackground(theme, theme.backupRowBg)};
        }
        /* Confirmed: dim every cell like declined (incl. name → RSVP) so the row reads as one band. */
        .admin-guest-portal--sheet tr.admin-guest-portal__row--rsvp-confirmed > td {
          opacity: 0.5;
          filter: grayscale(0.35);
        }
        .admin-guest-portal--sheet tr.admin-guest-portal__row--rsvp-confirmed > td:nth-child(5) input[type='text'],
        .admin-guest-portal--sheet tr.admin-guest-portal__row--rsvp-confirmed > td:nth-child(6) input[type='text'] {
          color: ${theme.rsvp.confirmed} !important;
          font-style: italic !important;
        }
        .admin-guest-portal__guest-chrome {
          padding-left: max(16px, env(safe-area-inset-left));
          padding-right: max(16px, env(safe-area-inset-right));
        }
        .admin-guest-portal__overview-chip-btn:focus {
          outline: none;
        }
        .admin-guest-portal__overview-chip-btn:focus-visible {
          outline: 2px solid ${theme.accent};
          outline-offset: 2px;
        }
        .admin-guest-portal__donut-confirmed-filter:hover {
          background: ${theme.mode === 'dark' ? 'rgba(74, 222, 128, 0.08)' : 'rgba(22, 163, 74, 0.07)'} !important;
        }
        .admin-guest-portal__donut-confirmed-filter:focus {
          outline: none;
        }
        .admin-guest-portal__donut-confirmed-filter:focus-visible {
          outline: 2px solid ${theme.rsvp.confirmed};
          outline-offset: 2px;
        }
        /* Sheet band: edge-to-edge with the admin body (no 16px gutters). Notch/home-indicator only. */
        .admin-guest-portal__table-outer {
          box-sizing: border-box;
          width: 100%;
          min-width: 0;
          padding-left: env(safe-area-inset-left, 0px);
          padding-right: env(safe-area-inset-right, 0px);
        }
        /* Horizontal padding lives on a parent (.guest-chrome); this node is only the x-scrollport so sticky name column + thead align with scroll, not padding quirks. */
        .admin-guest-portal__table-wrap {
          padding: 0;
          margin: 0;
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          overflow-y: visible;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-x: contain;
        }
        .admin-guest-portal__fab {
          display: none;
        }
        .admin-guest-portal__search-shell {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1 1 200px;
          max-width: 100%;
          padding: 2px 4px 2px 12px;
          border: 1px solid ${theme.border};
          border-radius: 10px;
          background: ${theme.bgInput};
          box-sizing: border-box;
        }
        .admin-guest-portal__search-shell:focus-within {
          border-color: ${theme.accent};
          box-shadow: 0 0 0 2px ${theme.accentTint};
        }
        .admin-guest-portal__search-shell input[type="search"] {
          border: none !important;
          background: transparent !important;
          box-shadow: none !important;
          padding-left: 0 !important;
          min-height: 34px;
        }
        @media (max-width: 640px) {
          .admin-guest-portal__body {
            padding-bottom: max(72px, env(safe-area-inset-bottom));
          }
          .admin-guest-portal th,
          .admin-guest-portal td {
            padding: 10px 8px;
            font-size: 14px;
          }
          .admin-guest-portal--sheet th,
          .admin-guest-portal--sheet td {
            padding: 4px 6px;
            font-size: 12px;
          }
          .admin-guest-portal__fab {
            display: flex;
            align-items: center;
            justify-content: center;
            position: fixed;
            z-index: 4;
            right: max(14px, env(safe-area-inset-right));
            bottom: max(14px, env(safe-area-inset-bottom));
            min-height: 48px;
            min-width: 48px;
            padding: 0 20px;
            border-radius: 999px;
            border: none;
            font: inherit;
            font-weight: 700;
            font-size: 15px;
            cursor: pointer;
            box-shadow: 0 4px 24px rgba(0, 0, 0, 0.28);
          }
        }
      `}</style>

      {registrySaveError ? (
        <div
          role="alert"
          style={{
            flexShrink: 0,
            margin: 0,
            padding: '12px 16px',
            background: theme.mode === 'dark' ? 'rgba(248, 113, 113, 0.14)' : '#fef2f2',
            color: theme.text,
            borderBottom: `1px solid ${theme.border}`,
            fontSize: 14,
            lineHeight: 1.45,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          <span style={{ flex: '1 1 240px' }}>
            <strong style={{ display: 'block', marginBottom: 4 }}>Cloud save failed</strong>
            {registrySaveError}
          </span>
          <button
            type="button"
            onClick={() => setRegistrySaveError(null)}
            style={{
              ...btnBase,
              flexShrink: 0,
              padding: '8px 14px',
              border: `1px solid ${theme.border}`,
              background: theme.bgInput,
              color: theme.text,
            }}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {showBrowserBackupHint ? (
        <div
          role="status"
          style={{
            flexShrink: 0,
            margin: 0,
            padding: '12px 16px',
            background: theme.mode === 'dark' ? 'rgba(251, 191, 36, 0.12)' : '#fffbeb',
            color: theme.text,
            borderBottom: `1px solid ${theme.border}`,
            fontSize: 14,
            lineHeight: 1.45,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          <span style={{ flex: '1 1 240px' }}>
            This browser has a larger guest list backup ({localBackupGuests.length} rows) than what is loaded (
            {guests.length}). If you lost rows after a failed cloud save, you can reload the backup here, then
            save again.
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                const draft = dedupeGuestsById(readGuestRegistryLocalStorageDraft());
                if (!draft.length) return;
                setGuests(draft);
                persistGuestsImmediate(draft);
                setBackupHintDismissed(true);
                setRegistrySaveError(null);
                pushToast('Loaded list from browser backup — watch for another save error if Firestore still fails.', 'info');
              }}
              style={{
                ...btnBase,
                padding: '8px 14px',
                border: `1px solid ${theme.border}`,
                background: theme.bgInput,
                color: theme.text,
                fontWeight: 700,
              }}
            >
              Use browser backup
            </button>
            <button
              type="button"
              onClick={() => setBackupHintDismissed(true)}
              style={{
                ...btnBase,
                padding: '8px 14px',
                border: `1px solid ${theme.border}`,
                background: 'transparent',
                color: theme.text,
                opacity: 0.85,
              }}
            >
              Hide
            </button>
          </div>
        </div>
      ) : null}

      <div
        className="admin-guest-portal__navigation"
        style={{
          flexShrink: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 8,
          padding: 'max(4px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) 0 max(16px, env(safe-area-inset-left))',
          borderBottom: `1px solid ${theme.border}`,
          background: theme.bgPanel,
        }}
      >
        <nav className="admin-guest-portal__tabs" role="tablist" aria-label="Admin sections">
          <button
            type="button"
            role="tab"
            aria-selected={adminTab === 'guests'}
            className={'admin-guest-portal__tab' + (adminTab === 'guests' ? ' admin-guest-portal__tab--active' : '')}
            onClick={() => setAdminTab('guests')}
          >
            Guests
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={adminTab === 'mailouts'}
            className={'admin-guest-portal__tab' + (adminTab === 'mailouts' ? ' admin-guest-portal__tab--active' : '')}
            onClick={() => setAdminTab('mailouts')}
          >
            Mailouts
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={adminTab === 'voicenotes'}
            className={
              'admin-guest-portal__tab' + (adminTab === 'voicenotes' ? ' admin-guest-portal__tab--active' : '')
            }
            onClick={() => setAdminTab('voicenotes')}
          >
            Voice notes
          </button>
        </nav>
        <button type="button" className="admin-guest-portal__mobile-only admin-guest-portal__menu-toggle"
          aria-label="Menu" title="Admin menu" aria-expanded={mobileMenuOpen} aria-controls="admin-account-actions"
          onClick={() => setMobileMenuOpen((v) => !v)}>⋯</button>
        <div id="admin-account-actions" className="admin-guest-portal__account-actions" style={{ display: 'flex', flexShrink: 0, gap: 8, flexWrap: 'wrap', paddingBottom: 6, alignItems: 'center' }}>
          <button
            type="button"
            onClick={downloadRegistryBackup}
            title="Download guest list and admin tag presets as JSON for disaster recovery"
            aria-label="Download full guest registry backup"
            style={{
              ...btnBase,
              padding: '8px 12px',
              border: `1px solid ${theme.border}`,
              background: theme.bgInput,
              color: theme.text,
              minHeight: 40,
              fontSize: 13,
            }}
          >
            Backup
          </button>
          <button
            type="button"
            onClick={toggleColorMode}
            aria-label={colorMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              ...btnBase,
              padding: '8px 12px',
              border: `1px solid ${theme.border}`,
              background: theme.bgInput,
              color: theme.text,
              minHeight: 40,
            }}
          >
            {colorMode === 'dark' ? 'Light' : 'Dark'}
          </button>
          {productionRemoteMode ? (
            <button
              type="button"
              onClick={() => {
                void import('./firebaseRemoteSync').then((m) =>
                  m.firebaseSignOut().then(() => onClose?.()),
                );
              }}
              style={{
                ...btnBase,
                padding: '8px 14px',
                border: `1px solid ${theme.border}`,
                background: theme.bgInput,
                color: theme.muted,
              }}
            >
              Sign out
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            style={{
              ...btnBase,
              padding: '8px 14px',
              border: `1px solid ${theme.border}`,
              background: theme.bgInput,
              color: theme.text,
            }}
          >
            Close
          </button>
        </div>
      </div>

      <div className="admin-guest-portal__body" ref={guestSheetScrollRef}>
        <div className="admin-guest-portal__body-inner">
          {adminTab === 'mailouts' ? (
            <>
              <header
                style={{
                  padding:
                    '10px max(16px, env(safe-area-inset-right)) 12px max(16px, env(safe-area-inset-left))',
                  borderBottom: `1px solid ${theme.border}`,
                  background: theme.bgPanel,
                }}
              >
                <h1
                  id="admin-guest-portal-title"
                  style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.text }}
                >
                  Mailouts
                </h1>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: theme.muted, maxWidth: 560, lineHeight: 1.45 }}>
                  Filter recipients, preview merge tags, then open your mail app or copy BCC addresses.
                </p>
              </header>
              <AdminMailoutsTab guests={guests} tagsOrdered={tagPaletteOrdered} />
            </>
          ) : null}

          {adminTab === 'voicenotes' ? <AdminVoiceNotesTab /> : null}

          {adminTab === 'guests' ? (
            <>
              <div
                className="admin-guest-portal__guest-chrome admin-guest-portal__guest-toolbar"
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 20,
                  paddingTop: 8,
                  paddingBottom: 10,
                  borderBottom: `1px solid ${theme.border}`,
                  background: theme.bgPanel,
                  boxShadow: `0 1px 0 ${theme.border}`,
                }}
              >
                <div className="admin-guest-portal__guest-heading"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'baseline',
                    gap: '6px 12px',
                    marginBottom: 8,
                  }}
                >
                  <h1
                    id="admin-guest-portal-title"
                    style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.text, letterSpacing: '-0.02em' }}
                    title="Edits save automatically. Click column headers to sort. Use tags to organise and filter."
                  >
                    Guest list
                  </h1>
                  {productionRemoteMode ? (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        color: theme.accent,
                        padding: '2px 8px',
                        borderRadius: 999,
                        border: `1px solid ${theme.liveBannerBorder}`,
                        background: theme.liveBannerBg,
                      }}
                      title="Saves sync to Firebase and the public site immediately."
                    >
                      LIVE
                    </span>
                  ) : null}
                  <span style={{ fontSize: 12, color: theme.muted, lineHeight: 1.35 }} aria-label="Guest count">
                    <strong style={{ color: theme.text }}>{filteredSorted.length}</strong>
                    {filteredSorted.length !== guests.length ? ` of ${guests.length}` : ''} guest
                    {guests.length === 1 ? '' : 's'}
                  </span>
                  {isRemoteGuestRegistryConfigured() ? (
                    <label className="admin-guest-portal__advanced"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        marginLeft: 4,
                        fontSize: 11,
                        color: theme.muted,
                        cursor: 'pointer',
                        maxWidth: 420,
                        lineHeight: 1.35,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={shrinkGuardBypass}
                        onChange={(ev) => {
                          const on = ev.target.checked;
                          setShrinkGuardBypass(on);
                          setGuestRegistryShrinkGuardBypass(on);
                        }}
                      />
                      <span>
                        Allow large guest-list reductions (turn on only when you intend to remove many rows; clears
                        after the next successful cloud save)
                      </span>
                    </label>
                  ) : null}
                </div>
                <div id="admin-guest-tools" className="admin-guest-portal__tools"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 8,
                    rowGap: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={addGuest}
                    style={{
                      ...btnBase,
                      padding: '6px 12px',
                      border: 'none',
                      background: theme.accent,
                      color: theme.onAccent,
                      minHeight: 36,
                      fontSize: 13,
                    }}
                  >
                    + Add
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportModalOpen(true)}
                    style={{
                      ...btnBase,
                      padding: '6px 12px',
                      border: `1px solid ${theme.border}`,
                      background: theme.bgInput,
                      color: theme.text,
                      minHeight: 36,
                      fontSize: 13,
                    }}
                  >
                    Import
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      downloadVendorGuestExport(guests);
                      pushToast('Exported guest list CSV', 'success');
                    }}
                    title="Full registry CSV for vendors."
                    style={{
                      ...btnBase,
                      padding: '6px 12px',
                      border: `1px solid ${theme.border}`,
                      background: theme.bgInput,
                      color: theme.text,
                      minHeight: 36,
                      fontSize: 13,
                    }}
                  >
                    Export
                  </button>
                  <label
                    style={{
                      ...btnBase,
                      padding: '6px 12px',
                      border: `1px dashed ${theme.border}`,
                      background: theme.bgMuted,
                      color: theme.muted,
                      cursor: 'pointer',
                      display: 'inline-block',
                      minHeight: 36,
                      fontSize: 13,
                    }}
                  >
                    <input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      hidden
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (!f) return;
                        const reader = new FileReader();
                        reader.onload = () => {
                          const text = typeof reader.result === 'string' ? reader.result : '';
                          setImportBootstrapText(text);
                          setImportModalOpen(true);
                          pushToast('CSV loaded — review import', 'info');
                        };
                        reader.readAsText(f);
                      }}
                    />
                    CSV file…
                  </label>
                  <label
                    className="admin-guest-portal__search-shell"
                    htmlFor="admin-guest-search"
                    style={{ flex: '1 1 min(100%, 240px)', minWidth: 0, maxWidth: 520 }}
                  >
                    <IconSearch color={theme.muted} />
                    <input
                      id="admin-guest-search"
                      type="search"
                      placeholder="Search name, email, tags, notes…"
                      value={searchRaw}
                      onChange={(e) => setSearchRaw(e.target.value)}
                      aria-label="Search guests"
                      autoComplete="off"
                    />
                  </label>
                  <span
                    role="separator"
                    aria-hidden
                    style={{
                      width: 1,
                      height: 24,
                      background: theme.border,
                      flexShrink: 0,
                      margin: '0 2px',
                    }}
                  />
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="admin-guest-portal__desktop-selection"
                    disabled={!selectedCount}
                    style={{
                      ...btnBase,
                      padding: '6px 10px',
                      fontSize: 12,
                      border: `1px solid ${theme.border}`,
                      background: theme.bgInput,
                      color: theme.text,
                      minHeight: 36,
                    }}
                  >
                    Clear ({selectedCount})
                  </button>
                  <button
                    type="button"
                    onClick={removeSelectedGuests}
                    className="admin-guest-portal__desktop-selection"
                    disabled={!selectedCount}
                    style={{
                      ...btnBase,
                      padding: '6px 10px',
                      fontSize: 12,
                      border: `1px solid ${theme.border}`,
                      background: theme.bgInput,
                      color: theme.danger,
                      minHeight: 36,
                    }}
                  >
                    Remove…
                  </button>
                  <button
                    type="button"
                    onClick={resetListFilters}
                    style={{
                      ...btnBase,
                      padding: '6px 10px',
                      fontSize: 12,
                      border: `1px solid ${theme.border}`,
                      background: theme.bgMuted,
                      color: theme.muted,
                      minHeight: 36,
                    }}
                  >
                    Reset filters
                  </button>
                </div>

                <div className="admin-guest-portal__filters"
                  style={{
                    marginTop: 10,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 8,
                    rowGap: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.07em',
                      color: theme.muted,
                    }}
                  >
                    SITE
                  </span>
                  <div
                    style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}
                    role="group"
                    aria-label="Filter by ceremony or Penicuik"
                  >
                    <button
                      type="button"
                      onClick={toggleCeremonyFilter}
                      aria-pressed={filterCeremony}
                      title="Show only ceremony guests (star column)"
                      style={{
                        ...btnBase,
                        fontSize: 12,
                        padding: '4px 10px',
                        borderRadius: 999,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        border: `1px solid ${
                          filterCeremony ? ceremonyGuestStarColor(theme) : theme.border
                        }`,
                        background: filterCeremony ? ceremonyGuestRowTint(theme) : theme.bgPanel,
                        color: theme.text,
                        fontWeight: 500,
                        minHeight: 30,
                      }}
                    >
                      <SFSymbolStar
                        filled={filterCeremony}
                        onColor={ceremonyGuestStarColor(theme)}
                        offColor={siteToggleIconOffColor(theme)}
                      />
                      Ceremony
                      <span style={{ color: theme.muted, marginLeft: 2 }}>
                        {siteFilterCounts.ceremony}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFilterPenicuik((v) => !v)}
                      aria-pressed={filterPenicuik}
                      title="Show only Penicuik stay guests (house column)"
                      style={{
                        ...btnBase,
                        fontSize: 12,
                        padding: '4px 10px',
                        borderRadius: 999,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        border: `1px solid ${filterPenicuik ? theme.indigo : theme.border}`,
                        background: filterPenicuik ? theme.indigoTint : theme.bgPanel,
                        color: theme.text,
                        fontWeight: 500,
                        minHeight: 30,
                      }}
                    >
                      <SFSymbolHouse
                        filled={filterPenicuik}
                        onColor={theme.indigo}
                        offColor={siteToggleIconOffColor(theme)}
                      />
                      Penicuik
                      <span style={{ color: theme.muted, marginLeft: 2 }}>
                        {siteFilterCounts.penicuik}
                      </span>
                    </button>
                  </div>
                  <div role="group" aria-label="Filter by RSVP or train" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {[
                      ['RSVP confirmed', filterRsvpConfirmed, toggleRsvpConfirmedFilter],
                      ['Train interested', filterTrainInterested, toggleTrainInterestedFilter],
                      ['Train confirmed', filterTrainConfirmed, toggleTrainConfirmedFilter],
                    ].map(([label, active, onClick]) => (
                      <button key={label} type="button" aria-pressed={active} onClick={onClick}
                        style={{ ...btnBase, fontSize: 12, padding: '4px 10px', borderRadius: 999,
                          border: `1px solid ${active ? theme.indigo : theme.border}`,
                          background: active ? theme.indigoTint : theme.bgPanel, color: theme.text, minHeight: 30 }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <span
                    role="separator"
                    aria-hidden
                    style={{
                      width: 1,
                      height: 22,
                      background: theme.border,
                      flexShrink: 0,
                      margin: '0 4px',
                    }}
                  />
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.07em',
                      color: theme.muted,
                    }}
                  >
                    TAGS
                  </span>
                  {tagFilterKeys.size > 0 ? (
                    <>
                      <span style={{ fontSize: 12, color: theme.muted }}>Matching any of {tagFilterKeys.size} selected tags</span>
                      <button type="button" onClick={() => setTagFilterKeys(new Set())}
                        style={{ ...btnBase, padding: '4px 10px', fontSize: 11, border: `1px solid ${theme.border}`, background: theme.bgPanel, color: theme.accent }}>
                        Clear tags
                      </button>
                    </>
                  ) : null}
                  <div
                    style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}
                    role="group"
                    aria-label="Filter by tag"
                  >
                    {tagPaletteOrdered.map(({ key, label, count }) => {
                      const active = tagFilterKeys.has(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleTagFilter(key)}
                          aria-pressed={active}
                          title="Toggle this tag filter. Guests matching any selected tag are shown."
                          style={{
                            ...btnBase,
                            fontSize: 12,
                            padding: '4px 10px',
                            borderRadius: 999,
                            border: `1px solid ${active ? theme.indigo : theme.border}`,
                            background: active ? theme.indigoTint : theme.bgPanel,
                            color: theme.text,
                            fontWeight: 500,
                            minHeight: 30,
                          }}
                        >
                          {label}
                          <span style={{ color: theme.muted, marginLeft: 6 }}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {selectedCount > 0 ? (
                  <div className="admin-guest-portal__bulk-tags"
                    style={{
                      marginTop: 8,
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 8,
                      rowGap: 6,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.07em',
                        color: theme.muted,
                      }}
                    >
                      BULK TAGS ({selectedCount})
                    </span>
                    <div
                      style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
                      role="group"
                      aria-label="Add preset tag to selected guests"
                    >
                      {presetPaletteForQuickAdd.map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => addTagToSelectedGuests(label)}
                          title={`Add “${label}” to all selected guests`}
                          style={{
                            ...btnBase,
                            fontSize: 11,
                            padding: '4px 8px',
                            borderRadius: 999,
                            border: `1px dashed ${theme.border}`,
                            background: theme.bgMuted,
                            color: theme.text,
                            fontWeight: 500,
                            minHeight: 28,
                          }}
                        >
                          + {label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                <div className="admin-guest-portal__mobile-only admin-guest-portal__list-controls">
                  <button type="button" aria-expanded={mobileToolsOpen} aria-controls="admin-guest-tools"
                    onClick={() => setMobileToolsOpen((v) => !v)}>
                    {mobileToolsOpen ? 'Hide filters' : 'Filters'}
                    {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                  </button>
                  <span role="status">{filteredSorted.length} shown{selectedCount > 0 ? ` · ${selectedCount} selected` : ''}</span>
                </div>
                {selectedCount > 0 ? <div className="admin-guest-portal__mobile-only admin-guest-portal__selection-actions">
                  <button type="button" onClick={clearSelection}>Clear selection</button>
                  <button type="button" onClick={removeSelectedGuests} style={{ color: theme.danger }}>Remove…</button>
                </div> : null}
              </div>

              <div
                className="admin-guest-portal__guest-chrome admin-guest-portal__overview"
                style={{
                  paddingTop: 10,
                  paddingBottom: 10,
                  borderBottom: `1px solid ${theme.border}`,
                  background: theme.bgMuted,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                {overviewStrip}
              </div>

              <div
                className="admin-guest-portal__table-outer"
                style={{
                  paddingTop: 0,
                  paddingBottom: 16,
                  background: theme.bgPanel,
                }}
              >
                <div className="admin-guest-portal__table-wrap">
                  <table className="admin-guest-portal--sheet" aria-label="Guest list" style={{ minWidth: 1380 }}>
            <thead>
              <tr>
                <th
                  style={{ width: 36, textAlign: 'center' }}
                  aria-label="Select guests"
                  title="Shift+click row checkboxes to select a range in the current list"
                >
                  <label className="admin-guest-portal__selection-hit">
                  <input
                    ref={headerSelectRef}
                    aria-label="Select all visible guests"
                    type="checkbox"
                    checked={allInViewSelected}
                    onChange={() => {
                      if (allInViewSelected) clearSelection();
                      else selectAllInView();
                    }}
                  />
                  </label>
                </th>
                <th
                  scope="col"
                  style={{ width: 7, padding: '4px 0', cursor: 'pointer', userSelect: 'none' }}
                  aria-label="Group colour. Sort by group / household."
                  title="Sort by group / household"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'group'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('group')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('group');
                    }
                  }}
                >
                  {guestListSort.key === 'group' ? (
                    <span style={{ fontSize: 9, color: theme.accent, fontWeight: 700 }} aria-hidden>
                      {guestListSort.asc ? '▲' : '▼'}
                    </span>
                  ) : (
                    <span
                      style={{
                        display: 'block',
                        width: 6,
                        height: 14,
                        margin: '0 auto',
                        borderRadius: 2,
                        background:
                          theme.mode === 'dark'
                            ? 'linear-gradient(180deg, hsl(200 35% 48%), hsl(280 35% 48%), hsl(40 40% 48%))'
                            : 'linear-gradient(180deg, hsl(200 50% 62%), hsl(280 45% 62%), hsl(40 55% 58%))',
                        opacity: 0.85,
                      }}
                      aria-hidden
                    />
                  )}
                </th>
                <th
                  scope="col"
                  style={{ width: 32, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  aria-label="Ceremony guest. Sort column."
                  title="Sort by ceremony guest"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'ceremony'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('ceremony')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('ceremony');
                    }
                  }}
                >
                  {guestListSort.key === 'ceremony' ? (
                    <span style={{ fontSize: 10, color: ceremonyGuestStarColor(theme), fontWeight: 700 }} aria-hidden>
                      {guestListSort.asc ? '▲' : '▼'}
                    </span>
                  ) : (
                    <span
                      style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        lineHeight: 1,
                        gap: 0,
                        fontSize: 7,
                        color: theme.mutedLight,
                        opacity: 0.85,
                      }}
                      aria-hidden
                    >
                      <span>▴</span>
                      <span>▾</span>
                    </span>
                  )}
                </th>
                <th
                  scope="col"
                  style={{ width: 32, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  aria-label="Staying at Penicuik. Sort column."
                  title="Sort by staying at Penicuik"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'penicuik'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('penicuik')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('penicuik');
                    }
                  }}
                >
                  {guestListSort.key === 'penicuik' ? (
                    <span style={{ fontSize: 10, color: theme.indigo, fontWeight: 700 }} aria-hidden>
                      {guestListSort.asc ? '▲' : '▼'}
                    </span>
                  ) : (
                    <span
                      style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        lineHeight: 1,
                        gap: 0,
                        fontSize: 7,
                        color: theme.mutedLight,
                        opacity: 0.85,
                      }}
                      aria-hidden
                    >
                      <span>▴</span>
                      <span>▾</span>
                    </span>
                  )}
                </th>
                <th
                  scope="col"
                  style={{ minWidth: 120, cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by first name"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'firstName'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('firstName')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('firstName');
                    }
                  }}
                >
                  Name
                </th>
                <th
                  scope="col"
                  style={{ minWidth: 100, cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by last name"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'lastName'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('lastName')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('lastName');
                    }
                  }}
                >
                  Last
                </th>
                <th
                  scope="col"
                  style={{
                    width: 36,
                    textAlign: 'center',
                    padding: '4px 2px',
                    fontSize: 8,
                    fontWeight: 700,
                    color: theme.mutedLight,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                  title="Site invite / Typeform URL sent (your manual record)"
                  aria-label="Invite sent"
                >
                  sent
                </th>
                <th
                  scope="col"
                  style={{ minWidth: 132, cursor: 'pointer', userSelect: 'none' }}
                  title="RSVP: To invite · Declined · Tentative · Yes (verbal) · Confirmed (form)"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'adminRsvp'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('adminRsvp')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('adminRsvp');
                    }
                  }}
                >
                  RSVP
                </th>
                <th
                  scope="col"
                  style={{ minWidth: 200, cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by tags"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'tags'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('tags')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('tags');
                    }
                  }}
                >
                  Tags
                </th>
                <th
                  scope="col"
                  style={{ width: 40, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  aria-label="Whose guest (Ben / Meg / Joint). Sort column."
                  title="Sort by whose guest (Ben / Meg / Joint)"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'who'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('who')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('who');
                    }
                  }}
                />
                <th
                  scope="col"
                  style={{ minWidth: 140, cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by email"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'email'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('email')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('email');
                    }
                  }}
                >
                  Email
                </th>
                <th
                  scope="col"
                  style={{ minWidth: 100, cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by phone"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'phone'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('phone')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('phone');
                    }
                  }}
                >
                  Phone
                </th>
                <th
                  scope="col"
                  style={{ minWidth: 72 }}
                  title="Pick a guest: household is named from their first + last name (or email if no name)"
                >
                  Group
                </th>
                <th
                  scope="col"
                  style={{ minWidth: 88, cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by accommodation"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'accommodation'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('accommodation')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('accommodation');
                    }
                  }}
                >
                  Accom.
                </th>
                <th
                  scope="col"
                  style={{ width: 118, textAlign: 'center' }}
                  title="Coach from Edinburgh at 2:30pm; tap return to cycle 10:30pm, 12:30am, or off. Columns: inbound pickup, return bus."
                >
                  Coach
                </th>
                <th
                  scope="col"
                  style={{ width: 72, textAlign: 'center' }}
                  title="Interested in group train travel on 29 December (Typeform RSVP). Tap to cycle Yes → No → unset."
                >
                  Train
                </th>
                <th
                  scope="col"
                  style={{ minWidth: 100, cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by dietary requirements"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'dietary'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('dietary')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('dietary');
                    }
                  }}
                >
                  Dietary
                </th>
                <th
                  scope="col"
                  style={{ minWidth: 160, cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by postal address"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'address'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('address')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('address');
                    }
                  }}
                >
                  Postal address
                </th>
                <th
                  scope="col"
                  style={{ minWidth: 88, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                  title="Sort by when RSVP was last updated (form / admin)"
                  tabIndex={0}
                  aria-sort={
                    guestListSort.key === 'rsvpUpdatedAt'
                      ? guestListSort.asc
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() => toggleGuestListSort('rsvpUpdatedAt')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGuestListSort('rsvpUpdatedAt');
                    }
                  }}
                >
                  RSVP updated
                  {guestListSort.key === 'rsvpUpdatedAt' ? (
                    <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 700 }} aria-hidden>
                      {guestListSort.asc ? '▲' : '▼'}
                    </span>
                  ) : null}
                </th>
                <th className="admin-guest-portal__mobile-sort">
                  <label htmlFor="admin-mobile-sort">Sort guests</label>
                  <select id="admin-mobile-sort" value={guestListSort.key || ''}
                    onChange={(e) => setGuestListSort({ key: e.target.value || null, asc: true })}>
                    <option value="">List order</option>
                    <option value="firstName">First name</option><option value="lastName">Last name</option>
                    <option value="adminRsvp">RSVP</option><option value="group">Household</option>
                    <option value="ceremony">Ceremony</option><option value="penicuik">Penicuik</option>
                    <option value="tags">Tags</option><option value="who">Whose guest</option>
                    <option value="email">Email</option><option value="phone">Phone</option>
                    <option value="accommodation">Accommodation</option><option value="dietary">Dietary requirements</option>
                    <option value="address">Postal address</option>
                    <option value="rsvpUpdatedAt">RSVP updated</option>
                  </select>
                  <button type="button" disabled={!guestListSort.key}
                    aria-label={guestListSort.asc ? 'Sort descending' : 'Sort ascending'}
                    onClick={() => setGuestListSort((v) => ({ ...v, asc: !v.asc }))}>{guestListSort.asc ? '↑' : '↓'}</button>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredSorted.length === 0 ? (
                <tr>
                  <td colSpan={19} style={{ padding: 40, textAlign: 'center', verticalAlign: 'middle' }}>
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 650, color: theme.text }}>
                      {guests.length === 0 ? 'No guests yet' : 'No guests match'}
                    </p>
                    <p
                      style={{
                        margin: '10px auto 0',
                        maxWidth: 420,
                        fontSize: 13,
                        color: theme.muted,
                        lineHeight: 1.5,
                      }}
                    >
                      {guests.length === 0
                        ? 'Use Add guest or Import CSV to build the list.'
                        : 'Try clearing search or tag filter.'}
                    </p>
                    {guests.length === 0 ? (
                      <div style={{ marginTop: 18, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 10 }}>
                        <button
                          type="button"
                          onClick={addGuest}
                          style={{
                            ...btnBase,
                            padding: '10px 18px',
                            border: 'none',
                            background: theme.accent,
                            color: theme.onAccent,
                            minHeight: 44,
                          }}
                        >
                          + Add guests
                        </button>
                        <button
                          type="button"
                          onClick={() => setImportModalOpen(true)}
                          style={{
                            ...btnBase,
                            padding: '10px 18px',
                            border: `1px solid ${theme.border}`,
                            background: theme.bgInput,
                            color: theme.text,
                          }}
                        >
                          Import CSV…
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ) : (
                filteredSorted.map((g) => (
                  <AdminGuestSheetRow
                    key={g.id}
                    guest={g}
                    isSelected={selectedIds.has(g.id)}
                    isEntering={enteringGuestIds.has(g.id)}
                    theme={theme}
                    btnBase={btnBase}
                    patchGuest={patchGuest}
                    addGuestTag={addGuestTag}
                    removeGuestTag={removeGuestTag}
                    addCustomTagPreset={addCustomTagPreset}
                    handleGuestRowCheckboxChange={handleGuestRowCheckboxChange}
                    onGroupAssign={handleGroupAssign}
                    rsvpAgoNowMs={rsvpAgoNowMs}
                  />
                ))
              )}
            </tbody>
          </table>
                </div>
              </div>
      <AdminCsvImportModal
        open={importModalOpen}
        onClose={() => {
          setImportModalOpen(false);
          setImportBootstrapText(null);
        }}
        guests={guests}
        onMerged={onImportMerged}
        newGuestId={newGuestId}
        btnBase={btnBase}
        initialCsvText={importBootstrapText}
        onConsumedBootstrap={() => setImportBootstrapText(null)}
      />
            </>
          ) : null}
        </div>
      </div>
      <div
        aria-live="polite"
        aria-relevant="additions text"
        style={{
          position: 'fixed',
          top: 'max(12px, env(safe-area-inset-top))',
          right: 'max(12px, env(safe-area-inset-right))',
          zIndex: 10006,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          alignItems: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`admin-guest-portal__toast admin-guest-portal__toast--${
              t.tone === 'error' ? 'error' : t.tone === 'success' ? 'success' : 'info'
            }`}
            onClick={() => dismissToast(t.id)}
            title="Dismiss"
            style={{ color: theme.text }}
          >
            {t.message}
          </button>
        ))}
      </div>

      {adminTab === 'guests' ? (
        <button
          type="button"
          className="admin-guest-portal__fab"
          onClick={addGuest}
          aria-label="Add guest"
          style={{
            background: theme.accent,
            color: theme.onAccent,
          }}
        >
          <span aria-hidden="true">+</span>
        </button>
      ) : null}
      <div
        ref={(el) => setTagSuggestPortalHost(el)}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 100100,
          pointerEvents: 'none',
        }}

      />
    </div>
    </TagPaletteRefContext.Provider>
    </AdminAllGuestsRefContext.Provider>
    </TagSuggestPortalHostContext.Provider>
  );
}

export function AdminGuestPortal(props) {
  const [colorMode, setColorMode] = useState(readAdminColorMode);
  useEffect(() => {
    writeAdminColorMode(colorMode);
  }, [colorMode]);
  const theme = useMemo(() => getAdminTheme(colorMode), [colorMode]);
  const toggleColorMode = useCallback(() => {
    setColorMode((m) => (m === 'dark' ? 'light' : 'dark'));
  }, []);
  const adminThemeCtx = useMemo(
    () => ({ theme, colorMode, setColorMode, toggleColorMode }),
    [theme, colorMode, toggleColorMode],
  );
  return (
    <AdminThemeProvider value={adminThemeCtx}>
      <AdminGuestPortalContent {...props} />
    </AdminThemeProvider>
  );
}
